import React from 'react';
import Flatpickr from 'react-flatpickr';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LabelList } from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
// Inline SVG so we can target the wordmark + swoosh paths individually from
// CSS for the run-state animation.
import { ReactComponent as DiradLogo } from '../img/DIRAD.svg';

const icon = 'menuChart';
const roles = { View: false };

const GRAN_LABELS = { hour: 'Hour', day: 'Day', week: 'Week', month: 'Month', year: 'Year', dow: 'Day of Week' };

// Cycled across pie slices. Anchored on the app blue, with contrasting hues that
// don't collide with the timeline action palette.
const PIE_COLORS = ['#2A6AA9', '#27a060', '#e67e22', '#8e44ad', '#c0392b', '#16a085', '#d35400', '#2c3e50', '#7f8c8d', '#f39c12', '#3498db', '#9b59b6'];

function formatScheduleSummary(sch) {
	if (!sch) return '';
	var t = sch.time || '00:00';
	var hm = t.split(':');
	var hr = parseInt(hm[0], 10);
	var min = hm[1] || '00';
	var ampm = hr >= 12 ? 'PM' : 'AM';
	var hr12 = hr === 0 ? 12 : (hr > 12 ? hr - 12 : hr);
	var clock = hr12 + ':' + min + ' ' + ampm;

	if (sch.frequency === 'daily') return 'Daily at ' + clock;
	if (sch.frequency === 'weekly') {
		var days = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
		return (days[parseInt(sch.dayOfWeek, 10)] || 'Weekly') + ' at ' + clock;
	}
	if (sch.frequency === 'monthly') {
		var d = parseInt(sch.dayOfMonth, 10);
		var suf = (d % 100 >= 11 && d % 100 <= 13) ? 'th'
			: (d % 10 === 1 ? 'st' : d % 10 === 2 ? 'nd' : d % 10 === 3 ? 'rd' : 'th');
		return d + suf + ' of each month at ' + clock;
	}
	return (sch.frequency || '') + ' at ' + clock;
}

const tableStyle = {
	width: '100%',
	borderCollapse: 'collapse',
	fontSize: '15px',
	fontFamily: 'Arial, sans-serif'
};
const thStyle = {
	padding: '5px 8px',
	textAlign: 'left',
	backgroundColor: '#2A6AA9',
	color: '#fff',
	fontWeight: 'bold',
	whiteSpace: 'nowrap',
	border: '1px solid #1e5080'
};
const tdStyle = {
	padding: '3px 8px',
	whiteSpace: 'nowrap',
	borderBottom: '1px solid #e0e0e0'
};
const trEvenStyle = { backgroundColor: '#f5f8fc', cursor: 'pointer' };
const trOddStyle = { backgroundColor: '#ffffff', cursor: 'pointer' };

// connectionMediaTypes.id for DiVA. The call-detail window switches to the tabbed
// AI view on this. Matches phone-router's `type: '8'` on the connection open and
// the `connection.type IN (1,8)` filter in advancedReports.js.
const DIVA_MEDIA_TYPE = 8;

// Interaction Log section colors — one per sub-bot in a call. Sub-bots carry no color
// of their own in the chatbot config, so these are handed out by first-appearance order
// within the call and cycle if a call uses more bots than there are colors.
//   bg     — the segment's unbroken background tint
//   bubble — the bot's speech bubble; a shade up from bg so it reads as sitting ON it
//   accent — left border, dots, and the bot's name. Darkened from the app palette
//            (e.g. #2A6AA9 -> #1f5c96) because it's used as small text on a light
//            tint, where the brighter originals fall under ~3:1 contrast.
const DIVA_BOT_COLORS = [
	{ bg: '#eef3f9', bubble: '#dbe8f6', accent: '#1f5c96' },
	{ bg: '#eaf6ef', bubble: '#d3eede', accent: '#1e7a4a' },
	{ bg: '#f3eefa', bubble: '#e6daf4', accent: '#6b3391' },
	{ bg: '#fdf3e0', bubble: '#f9e3bd', accent: '#8a5510' },
	{ bg: '#e9f6f5', bubble: '#d1eee9', accent: '#0f6f5f' },
	{ bg: '#fbecec', bubble: '#f6d9d6', accent: '#a32b20' }
];

// The recording subheader — a band directly under the navy call-detail header,
// above the tabs. flexShrink:0 pins it while the tab body below scrolls.
const recordingBarStyle = {
	display: 'flex',
	alignItems: 'center',
	gap: '12px',
	padding: '9px 24px',
	backgroundColor: '#eef3f9',
	borderBottom: '1px solid #dbe4ee',
	flexShrink: 0
};

// Color per action type for timeline badges
const ACTION_COLORS = {
	'Collect': '#2A6AA9',
	'Transfer': '#27a060',
	'Hangup': '#c0392b',
	'Play': '#8e44ad',
	'Queue': '#e67e22'
};
function actionColor(action) {
	return ACTION_COLORS[action] || '#555';
}

class AdvancedReports extends React.PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			activeReport: null,
			// Selected logger application id for the report dropdown. null ⇒ fall back to the
			// first available application (see effectiveApplication). Loaded from the logger DB
			// via getReportApplications; the dropdown is hidden when there's only one.
			application: null,
			parameters: {},
			pageSize: 100,
			activeCallID: null,
			activeCallPhone: null,
			activeCallStart: null,
			// Call-detail window (DiVA calls only — IVR calls have no tabs).
			// recordingRequested gates the lazy Twilio lookup to one call per open;
			// expandedTokenModels holds which model rows are showing their stage split.
			activeCallTab: 'interaction',
			recordingRequested: false,
			expandedTokenModels: {},
			// Custom recording player state, mirrored off the hidden <audio> element.
			audioPlaying: false,
			audioTime: 0,
			audioDuration: 0,
			sortCol: null,
			sortDir: 'asc',
			chartType: 'bar',
			drilldownMenu: null,
			schedulingReport: null,   // report config object whose schedule modal is open
			tempSchedule: null,        // form state inside the schedule modal
			scheduleSaving: false,
			schedulesManagerOpen: false,
			hoveredReportRow: null,
			// Dynamic connectionMeta columns added by the "+" picker on Interaction Detail.
			// extraMetaTypes is the working list of selected types; metaPickerOpen toggles the picker modal.
			extraMetaTypes: [],
			metaPickerOpen: false,
			// Inline filter-builder UI state for reports that take stacked filters
			// (currently Breadcrumb Summary). metaFilterDraft is the in-progress
			// form for the "add metadata filter" form.
			metaFilterDraft: null,
			// Toggled while exportPDF is capturing the DOM + assembling the PDF
			// so the button can show a busy state and avoid double-clicks.
			pdfGenerating: false
		};
	}

	componentDidMount() {
		// Load the application list once so the report dropdown is ready before the
		// user opens a report (and so single-application sites can auto-select it).
		if (this.reportApps().length === 0) {
			this.props.ws.send(JSON.stringify({ route: 'getReportApplications' }));
		}
	}

	componentDidUpdate() {
		// The recording strip sits above the tabs and shows on every call type, so its
		// uri is needed the moment the window opens — there's no tab click to wait for.
		// callSid only arrives with the advancedCallDetail response, so this is the
		// earliest we can ask. recordingRequested makes it fire once per open (and it's
		// reset in openCallDetail), which is also what keeps this setState from looping.
		this.maybeFetchRecording();
	}

	// Ask the server for this call's recording. Runs for ANY call with a Twilio CallSid,
	// not just DiVA ones and not just flagged ones — see renderRecordingBar for why the
	// connectionMeta 40 flag can't gate this.
	maybeFetchRecording = () => {
		if (!this.state.activeCallID || this.state.recordingRequested) return;
		var d = this.props.data ? this.props.data.advancedCallDetailData : null;
		if (!d || !d.callSid || d.recording) return;
		console.log('[AdvancedReports] recording: requesting advancedCallRecording for callSid', d.callSid,
			'| recorded(connectionMeta 40)=', !!d.recorded, '| callType=', d.callType);
		this.setState({ recordingRequested: true });
		this.props.ws.send(JSON.stringify({ route: 'advancedCallRecording', callSid: d.callSid, recorded: !!d.recorded }));
	}

	// The application list, delivered by the server inside the AdvancedReports module
	// object (a bare top-level setState key isn't in the view's data whitelist).
	reportApps = () => (this.props.data && this.props.data.AdvancedReports && this.props.data.AdvancedReports.applications) || [];

	// The application id a report run should use: the explicit selection when it's
	// still valid, otherwise the first available application. undefined when none are
	// loaded (server then falls back to its legacy config value).
	effectiveApplication = () => {
		var apps = this.reportApps();
		var sel = this.state.application;
		if (sel != null && apps.some((a) => String(a.id) === String(sel))) return sel;
		return apps.length ? apps[0].id : undefined;
	}

	selectReport = (report) => {
		var defaults = {};
		(report.parameters || []).forEach(function (p) {
			if (p.default !== undefined) defaults[p.parameter] = p.default;
		});
		this.setState({ activeReport: report, parameters: defaults, drilldownMenu: null, extraMetaTypes: [], metaPickerOpen: false, metaFilterDraft: null });
		this.props.ws.send(JSON.stringify({ route: 'setState', state: { advancedReportData: false, advancedReportLoading: false } }));
		// Load the application list on report open too (not just componentDidMount) — mount can
		// fire before the impersonation session/WebSocket is ready and drop the request.
		if (this.reportApps().length === 0) {
			this.props.ws.send(JSON.stringify({ route: 'getReportApplications' }));
		}
		// Lazy-load the connectionMetaTypes list when a report that uses it is
		// selected (Interaction Detail's "+" column picker, Breadcrumb Summary's
		// meta-filter form).
		var needsMetaTypes = report && (report.route === 'advancedIncomingCallsDetail' || report.route === 'advancedBreadcrumbSummary');
		if (needsMetaTypes && (!this.props.data || !this.props.data.connectionMetaTypes)) {
			this.props.ws.send(JSON.stringify({ route: 'getConnectionMetaTypes' }));
		}
		// Breadcrumb Summary's cohort filter is dropdown-driven — load the
		// breadcrumb labels and menu list when the report opens.
		if (report && report.route === 'advancedBreadcrumbSummary') {
			if (!this.props.data || !this.props.data.breadcrumbLabels) {
				this.props.ws.send(JSON.stringify({ route: 'getBreadcrumbLabels' }));
			}
			if (!this.props.data || !this.props.data.menus) {
				this.props.ws.send(JSON.stringify({ route: 'getMenus' }));
			}
		}
	}

	closeReport = () => {
		this.setState({ activeReport: null, parameters: {}, activeCallID: null });
	}

	openSchedule = (report) => (e) => {
		// Stop the row's click handler (which would open the report modal) from firing
		e.stopPropagation();
		// Seed param defaults. For datetime params on a recurring schedule, pick
		// sensible relative defaults (start = 7 days before run, end = run time)
		// instead of absolute dates that would never roll forward.
		var paramDefaults = {};
		(report.parameters || []).forEach(function (p) {
			if (p.default !== undefined) {
				paramDefaults[p.parameter] = p.default;
			} else if (p.type === 'datetime') {
				if (p.parameter === 'start') paramDefaults[p.parameter] = { mode: 'relative', daysAgo: 7 };
				else if (p.parameter === 'end') paramDefaults[p.parameter] = { mode: 'now' };
			}
		});
		this.setState({
			schedulingReport: report,
			tempSchedule: {
				frequency: 'daily',
				time: '09:00',
				dayOfWeek: 1,           // Monday for weekly
				dayOfMonth: 1,           // 1st of month for monthly
				recipients: ''
			},
			tempScheduleParams: paramDefaults,
			scheduleSaving: false
		});
	}

	setScheduleParam = (key, value) => {
		this.setState(function (s) {
			var p = Object.assign({}, s.tempScheduleParams || {});
			p[key] = value;
			return { tempScheduleParams: p };
		});
	}

	closeSchedule = () => {
		this.setState({ schedulingReport: null, tempSchedule: null, tempScheduleParams: null, scheduleSaving: false });
	}

	editSchedule = (field) => (e) => {
		var v = e.target.value;
		this.setState(function (s) {
			return { tempSchedule: Object.assign({}, s.tempSchedule, (function () { var o = {}; o[field] = v; return o; })()) };
		});
	}

	openSchedulesManager = () => {
		this.setState({ schedulesManagerOpen: true });
	}

	closeSchedulesManager = () => {
		this.setState({ schedulesManagerOpen: false });
	}

	deleteSchedule = (id) => () => {
		this.props.ws.send(JSON.stringify({ route: 'deleteAdvancedReportSchedule', id: id }));
	}

	saveSchedule = () => {
		var rep = this.state.schedulingReport;
		var sch = this.state.tempSchedule;
		if (!rep || !sch) return;
		// Capture the current application selection into the schedule's params so the
		// recurring run queries the same application (server reads data.application).
		var schedParams = Object.assign({}, this.state.tempScheduleParams || {});
		var appId = this.effectiveApplication();
		if (appId !== undefined && schedParams.application === undefined) schedParams.application = appId;
		this.props.ws.send(JSON.stringify({
			route: 'saveAdvancedReportSchedule',
			reportRoute: rep.route,
			reportName: rep.name,
			frequency: sch.frequency,
			time: sch.time,
			dayOfWeek: sch.dayOfWeek,
			dayOfMonth: sch.dayOfMonth,
			recipients: sch.recipients,
			parameters: schedParams
		}));
		this.closeSchedule();
	}

	setParam = (key, value) => {
		var params = Object.assign({}, this.state.parameters);
		params[key] = value;
		this.setState({ parameters: params });
	}

	buildPayload = (page) => {
		var p = this.state.parameters;
		var rep = this.state.activeReport;
		var payload = {
			route: rep.route,
			page: page,
			pageSize: this.state.pageSize
		};
		// Carry the row count forward when paging — server skips the COUNT(*)
		// scan if a knownTotal is supplied and page > 0.
		var current = this.props.advancedReportData;
		if (page > 0 && current && current.table && typeof current.table.total === 'number') {
			payload.knownTotal = current.table.total;
		}
		var declared = {};
		(rep.parameters || []).forEach(function (param) {
			declared[param.parameter] = true;
			var key = param.parameter;
			var val = p[key];
			if (param.type === 'datetime') {
				payload[key] = val ? { datetime: val } : undefined;
			} else {
				payload[key] = (val === '' || val == null) ? undefined : val;
			}
		});
		// Pass through chip-driven filters (cohortBreadcrumbs, metaFilters, etc.)
		// — these live in state but aren't declared in the report's parameters,
		// so they need an explicit pass-through.
		Object.keys(p).forEach(function (k) {
			if (declared[k]) return;
			var v = p[k];
			if (v == null) return;
			if (Array.isArray(v) && v.length === 0) return;
			if (typeof v === 'string' && v === '') return;
			payload[k] = v;
		});
		// Only Interaction Detail honors extraMetaTypes on the server; including
		// it on other reports is harmless but pointless.
		if (rep.route === 'advancedIncomingCallsDetail' && this.state.extraMetaTypes && this.state.extraMetaTypes.length) {
			payload.extraMetaTypes = this.state.extraMetaTypes;
		}
		// Which logger application to query (dropdown selection, or the sole app auto-
		// selected when there's only one). Server falls back to config if unset.
		var appId = this.effectiveApplication();
		if (appId !== undefined) payload.application = appId;
		return payload;
	}

	addMetaColumn = (type) => {
		if (!type || type.id == null) return;
		// Avoid duplicates
		if (this.state.extraMetaTypes.some(function (m) { return m.id === type.id; })) {
			this.setState({ metaPickerOpen: false });
			return;
		}
		var next = this.state.extraMetaTypes.concat([{ id: type.id, name: type.name }]);
		this.setState({ extraMetaTypes: next, metaPickerOpen: false }, () => {
			this.props.ws.send(JSON.stringify(this.buildPayload(0)));
		});
	}

	removeMetaColumn = (id) => {
		var next = this.state.extraMetaTypes.filter(function (m) { return m.id !== id; });
		this.setState({ extraMetaTypes: next }, () => {
			this.props.ws.send(JSON.stringify(this.buildPayload(0)));
		});
	}

	runReport = () => {
		this.setState({ sortCol: null, sortDir: 'asc', drilldownMenu: null });
		this.props.ws.send(JSON.stringify(this.buildPayload(0)));
	}

	exportReport = () => {
		var payload = this.buildPayload(0);
		// Server uses `reportRoute` to dispatch; export route ignores page/pageSize
		payload.reportRoute = payload.route;
		payload.route = 'advancedReportExport';
		this.props.ws.send(JSON.stringify(payload));
	}

	// Ref to the rendered report-content area (filter chips + KPIs + chart +
	// table). Captured by exportPDF via html2canvas.
	reportContentRef = React.createRef()

	// Drives the custom recording player in the call-detail subheader. The <audio>
	// element is rendered without `controls` and never shown — this ref is the only
	// thing that touches it, and the strip's own UI reflects its state.
	audioRef = React.createRef()

	// Seconds -> m:ss for the player's elapsed/duration readouts. Guards NaN/Infinity,
	// which is what `duration` reads as until loadedmetadata fires.
	formatAudioTime = (secs) => {
		if (secs == null || isNaN(secs) || !isFinite(secs)) return '0:00';
		var s = Math.max(0, Math.floor(secs));
		var m = Math.floor(s / 60);
		var r = s % 60;
		return m + ':' + (r < 10 ? '0' : '') + r;
	}

	toggleRecording = () => {
		var a = this.audioRef.current;
		if (!a) return;
		if (a.paused) {
			// play() rejects on autoplay policy / a bad media URL — surface it rather
			// than leaving the button looking stuck.
			var p = a.play();
			if (p && p.catch) p.catch((e) => {
				console.log('[AdvancedReports] recording: play() failed —', e && e.message ? e.message : e);
				this.setState({ audioPlaying: false });
			});
		} else {
			a.pause();
		}
	}

	// Click anywhere on the track to scrub there.
	seekRecording = (e) => {
		var a = this.audioRef.current;
		var dur = this.state.audioDuration;
		if (!a || !dur) return;
		var rect = e.currentTarget.getBoundingClientRect();
		if (!rect.width) return;
		var pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
		a.currentTime = pct * dur;
		this.setState({ audioTime: a.currentTime });
	}

	// Client-side PDF: capture the rendered report area to a canvas, then place
	// it into a jsPDF doc with a small header. For tall reports the canvas is
	// sliced into page-sized chunks so each PDF page shows a clean slab — no
	// half-rows split across pages would also be nice, but slicing on pixel
	// rows is simple and good enough.
	exportPDF = async () => {
		var node = this.reportContentRef.current;
		if (!node) return;
		if (this.state.pdfGenerating) return;
		this.setState({ pdfGenerating: true });
		try {
			var canvas = await html2canvas(node, {
				scale: 2,
				backgroundColor: '#ffffff',
				useCORS: true,
				logging: false,
				// Skip nodes flagged with data-pdf-hide — chart-type dropdown,
				// "+ Add filter" controls, pagination buttons, the Export
				// CSV/PDF buttons, and inline "Tip:" hints. These are
				// interactive chrome that doesn't belong in the printed output.
				ignoreElements: function (el) {
					return el && el.getAttribute && el.getAttribute('data-pdf-hide') === 'true';
				}
			});
			var pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
			var pageWidth  = pdf.internal.pageSize.getWidth();
			var pageHeight = pdf.internal.pageSize.getHeight();
			var margin = 36;
			var usableWidth = pageWidth - 2 * margin;

			var reportData = this.props.advancedReportData || {};
			var rep = this.state.activeReport || {};
			var title = reportData.title || rep.name || 'Report';

			var params = this.state.parameters || {};
			var subParts = [];
			if (params.start) subParts.push('From ' + params.start);
			if (params.end)   subParts.push('to '   + params.end);
			subParts.push('Generated ' + new Date().toLocaleString());

			pdf.setFontSize(16);
			pdf.text(title, margin, margin + 12);
			pdf.setFontSize(10);
			pdf.setTextColor(120);
			pdf.text(subParts.join('  •  '), margin, margin + 28);
			pdf.setTextColor(0);

			var startY = margin + 44;
			var canvasY = 0;
			var pageNum = 0;
			while (canvasY < canvas.height) {
				if (pageNum > 0) pdf.addPage();
				var yTop = (pageNum === 0) ? startY : margin;
				// pixels of canvas height that fit in the remaining usable area
				// of this page after scaling to usableWidth.
				var availablePtH = pageHeight - yTop - margin;
				var availablePxH = availablePtH * canvas.width / usableWidth;
				var sliceH = Math.min(availablePxH, canvas.height - canvasY);

				var slice = document.createElement('canvas');
				slice.width  = canvas.width;
				slice.height = Math.ceil(sliceH);
				slice.getContext('2d').drawImage(canvas, 0, -canvasY);
				var imgData = slice.toDataURL('image/png');
				var slicePtH = slice.height * usableWidth / canvas.width;
				pdf.addImage(imgData, 'PNG', margin, yTop, usableWidth, slicePtH);

				canvasY += slice.height;
				pageNum++;
			}

			var safeTitle = String(title).toLowerCase()
				.replace(/[^a-z0-9]+/g, '-')
				.replace(/^-|-$/g, '')
				.slice(0, 50) || 'report';
			var stamp = new Date().toISOString().slice(0, 10);
			pdf.save(safeTitle + '-' + stamp + '.pdf');
		} catch (e) {
			console.error('[AdvancedReports] PDF export failed:', e);
		} finally {
			this.setState({ pdfGenerating: false });
		}
	}

	changePage = (newPage) => {
		this.props.ws.send(JSON.stringify(this.buildPayload(newPage)));
	}

	// Interactive filters — server reports can ship `chart.appendFilterOnClick`
	// and `table.rowAppendFilter` hints to turn chart/row clicks into "add this
	// to the named filter array and re-run". Filter chips × call removeFilter.
	// Re-runs the report at page 0, but only if results are already on screen.
	// Pre-run, filter changes just update state — the user picks filters first,
	// then clicks Start.
	rerunIfShowing = () => {
		if (this.props.advancedReportData) {
			this.props.ws.send(JSON.stringify(this.buildPayload(0)));
		}
	}

	appendFilterValue = (paramName, value) => {
		if (value == null || String(value) === '') return;
		var next = Object.assign({}, this.state.parameters);
		var current = Array.isArray(next[paramName]) ? next[paramName].slice() : [];
		if (current.indexOf(value) === -1) current.push(value);
		next[paramName] = current;
		this.setState({ parameters: next, sortCol: null, sortDir: 'asc', drilldownMenu: null }, this.rerunIfShowing);
	}

	appendMetaFilter = (typeId, typeName, value) => {
		var id = parseInt(typeId, 10);
		if (!Number.isFinite(id) || value == null || String(value) === '') return;
		var next = Object.assign({}, this.state.parameters);
		var current = Array.isArray(next.metaFilters) ? next.metaFilters.slice() : [];
		var key = id + ':' + String(value);
		var dupe = current.some(function (m) { return (m.typeId + ':' + m.value) === key; });
		if (!dupe) current.push({ typeId: id, typeName: typeName || ('Type ' + id), value: String(value) });
		next.metaFilters = current;
		this.setState({ parameters: next, metaFilterDraft: null, sortCol: null, sortDir: 'asc', drilldownMenu: null }, this.rerunIfShowing);
	}

	// Append a menu filter (cohortMenus / includeMenus). Each filter is just a
	// menu description — no option attached. `menu` is the flowLocation
	// description, so menus sharing a description across duplicate IDs collapse.
	appendMenuFilter = (paramName, mo) => {
		if (!mo || mo.menu == null) return;
		var next = Object.assign({}, this.state.parameters);
		var current = Array.isArray(next[paramName]) ? next[paramName].slice() : [];
		var menu = String(mo.menu);
		var dupe = current.some(function (x) { return String(x.menu) === menu; });
		if (!dupe) current.push({ menu: menu, label: mo.label || menu });
		next[paramName] = current;
		this.setState({ parameters: next, sortCol: null, sortDir: 'asc', drilldownMenu: null }, this.rerunIfShowing);
	}

	// Removes one filter chip by itemKey. Scalar strings compare directly;
	// metaFilters and menu arrays use composite keys.
	removeFilter = (paramName, itemKey) => {
		var next = Object.assign({}, this.state.parameters);
		var current = next[paramName];
		if (Array.isArray(current)) {
			if (paramName === 'metaFilters') {
				next[paramName] = current.filter(function (m) { return (m.typeId + ':' + m.value) !== itemKey; });
			} else if (paramName === 'cohortMenus' || paramName === 'includeMenus') {
				next[paramName] = current.filter(function (m) { return String(m.menu) !== itemKey; });
			} else {
				next[paramName] = current.filter(function (v) { return v !== itemKey; });
			}
			if (next[paramName].length === 0) delete next[paramName];
		} else {
			delete next[paramName];
		}
		this.setState({ parameters: next, sortCol: null, sortDir: 'asc', drilldownMenu: null }, this.rerunIfShowing);
	}

	handleSort = (colIdx) => {
		this.setState(function (prev) {
			return {
				sortCol: colIdx,
				sortDir: prev.sortCol === colIdx && prev.sortDir === 'asc' ? 'desc' : 'asc'
			};
		});
	}

	openCallDetail = (callID, phone, start) => {
		console.log('[AdvancedReports] row clicked callID:', callID, 'phone:', phone);
		// Always land on the Interaction Log and drop the previous call's tab state — otherwise
		// the next call opens on whatever tab the last one was left on, showing stale
		// recording/token state until its own data lands.
		this.setState({ activeCallID: callID, activeCallPhone: phone, activeCallStart: start, activeCallTab: 'interaction', recordingRequested: false, expandedTokenModels: {}, audioPlaying: false, audioTime: 0, audioDuration: 0 });
		this.props.ws.send(JSON.stringify({ route: 'advancedCallDetail', connectionID: callID }));
	}

	// Switch tabs in the DiVA call-detail window. The recording is fetched lazily the
	// first time its tab is opened — no point hitting Twilio for calls nobody listens to.
	// Switch tabs in the DiVA call-detail window. The recording is no longer one of them —
	// it's fetched on open (componentDidUpdate → maybeFetchRecording) and rendered above.
	selectCallTab = (key) => {
		this.setState({ activeCallTab: key });
	}

	closeCallDetail = () => {
		this.setState({ activeCallID: null, activeCallPhone: null, activeCallStart: null, activeCallTab: 'interaction', recordingRequested: false, expandedTokenModels: {}, audioPlaying: false, audioTime: 0, audioDuration: 0 });
	}

	formatPhone = (num) => {
		if (!num) return '';
		var n = String(num).replace(/\D/g, '');
		if (n.length === 7) return n.substring(0, 3) + '-' + n.substring(3, 7);
		if (n.length === 10) return '(' + n.substring(0, 3) + ') ' + n.substring(3, 6) + '-' + n.substring(6, 10);
		if (n.length === 11) return '(' + n.substring(1, 4) + ') ' + n.substring(4, 7) + '-' + n.substring(7, 11);
		if (n.length === 12) return '(' + n.substring(2, 5) + ') ' + n.substring(5, 8) + '-' + n.substring(8, 12);
		return num;
	}

	parseUTC = (val) => {
		if (!val) return null;
		var s = String(val);
		// MySQL DATETIME has no timezone marker — force UTC so browser localizes correctly
		if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) s = s.replace(' ', 'T') + 'Z';
		return new Date(s);
	}

	formatDateTime = (val) => {
		if (!val) return '';
		var d = this.parseUTC(val);
		return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
	}

	formatTime = (val) => {
		if (!val) return '';
		return this.parseUTC(val).toLocaleTimeString();
	}

	elapsed = (base, ts) => {
		if (!base || !ts) return '';
		var diff = Math.round((this.parseUTC(ts) - this.parseUTC(base)) / 1000);
		if (diff < 0) diff = 0;
		var m = Math.floor(diff / 60);
		var s = diff % 60;
		return '+' + m + ':' + (s < 10 ? '0' : '') + s;
	}

	// Schedule-mode param renderer: for datetime params, supports three modes —
	// absolute date, relative offset, or "when the report runs". For other types,
	// defers to the regular renderParamInput.
	renderScheduleParamInput = (param, paramInputStyle) => {
		var key = param.parameter;
		var current = (this.state.tempScheduleParams || {})[key];

		if (param.type !== 'datetime') {
			return this.renderParamInput(param, {
				value: current,
				setValue: (v) => this.setScheduleParam(key, v),
				inputStyle: paramInputStyle
			});
		}

		var mode = (current && current.mode) || 'absolute';
		var value = current && current.value;
		var daysAgo = current && current.daysAgo;
		var setMode = (newMode) => {
			var v;
			if (newMode === 'absolute') v = { mode: 'absolute', value: '' };
			else if (newMode === 'relative') v = { mode: 'relative', daysAgo: 7 };
			else v = { mode: 'now' };
			this.setScheduleParam(key, v);
		};

		var modeSelectStyle = Object.assign({}, paramInputStyle, { marginBottom: '6px' });

		return (
			<div>
				<select value={mode} onChange={(e) => setMode(e.target.value)} style={modeSelectStyle}>
					<option value="absolute">Specific date/time</option>
					<option value="relative">Relative to run time</option>
					<option value="now">When the report runs</option>
				</select>
				{mode === 'absolute' && (
					<Flatpickr
						data-enable-time
						options={{ dateFormat: 'Y-m-d h:i K', defaultHour: key === 'end' ? 23 : 0, defaultMinute: key === 'end' ? 59 : 0 }}
						value={value || null}
						onChange={([d]) => this.setScheduleParam(key, { mode: 'absolute', value: d ? d.toISOString() : '' })}
						style={paramInputStyle}
					/>
				)}
				{mode === 'relative' && (
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<input
							type="number"
							min="0"
							value={daysAgo != null ? daysAgo : ''}
							onChange={(e) => this.setScheduleParam(key, { mode: 'relative', daysAgo: parseInt(e.target.value || 0, 10) })}
							style={Object.assign({}, paramInputStyle, { width: '90px', marginBottom: 0 })}
						/>
						<span style={{ fontSize: '12px', color: '#666' }}>days before run time</span>
					</div>
				)}
				{mode === 'now' && (
					<div style={{ fontSize: '12px', color: '#888', padding: '6px 0' }}>
						Will use the report's run time.
					</div>
				)}
			</div>
		);
	}

	renderParamInput = (param, override) => {
		override = override || {};
		var key = param.parameter;
		var value = override.value !== undefined ? override.value : this.state.parameters[key];
		var setValue = override.setValue || ((v) => this.setParam(key, v));
		var inputStyle = override.inputStyle || { height: '60px', fontSize: '20px', padding: '0 12px', width: '100%', boxSizing: 'border-box' };

		if (param.type === 'datetime') {
			var isEnd = key === 'end';
			return (
				<Flatpickr
					key={param.label}
					data-enable-time
					options={{ dateFormat: 'Y-m-d h:i K', defaultHour: isEnd ? 23 : 0, defaultMinute: isEnd ? 59 : 0 }}
					value={value || null}
					onChange={([date]) => setValue(date ? date.toISOString() : null)}
					style={inputStyle}
				/>
			);
		} else if (param.type === 'text') {
			return (
				<input
					key={param.label}
					type="text"
					value={value || ''}
					onChange={(e) => setValue(e.target.value)}
					style={inputStyle}
				/>
			);
		} else if (param.type === 'select') {
			var opts = param.options || [];
			return (
				<select
					key={param.label}
					value={value != null ? value : (param.default != null ? param.default : (opts[0] && opts[0].value))}
					onChange={(e) => setValue(e.target.value)}
					style={inputStyle}
				>
					{opts.map((opt) => (
						<option key={opt.value} value={opt.value}>{opt.label}</option>
					))}
				</select>
			);
		}
		return null;
	}

	renderHeatmap = (heatmap) => {
		if (!heatmap || !heatmap.cells || heatmap.cells.length === 0) return null;

		// Lookup grid keyed by [dow][hr] for O(1) cell rendering
		var grid = {};
		heatmap.cells.forEach(function (c) {
			if (!grid[c.dow]) grid[c.dow] = {};
			grid[c.dow][c.hr] = c.count;
		});

		var max = heatmap.maxCount || 1;
		var dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

		return (
			<div style={{ marginBottom: '20px' }}>
				<div style={{ fontSize: '15px', fontWeight: 'bold', color: '#2A6AA9', marginBottom: '8px' }}>
					Calls by Hour & Day of Week
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
					{/* Hour-of-day header row */}
					<div style={{ display: 'flex', gap: '2px' }}>
						<div style={{ width: '38px', flexShrink: 0 }} />
						{Array.from({ length: 24 }, (_, h) => (
							<div key={h} style={{ flex: 1, textAlign: 'center', fontSize: '10px', color: '#888' }}>
								{h}
							</div>
						))}
					</div>
					{dayLabels.map((day, di) => (
						<div key={di} style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
							<div style={{ width: '38px', flexShrink: 0, fontSize: '11px', color: '#555', textAlign: 'right', paddingRight: '6px' }}>
								{day}
							</div>
							{Array.from({ length: 24 }, (_, hr) => {
								var v = (grid[di] && grid[di][hr]) || 0;
								// 0 = neutral grey, otherwise scale opacity from 0.15 → 1.0 against #2A6AA9
								var bg = v === 0
									? '#f0f0f0'
									: 'rgba(42, 106, 169, ' + (0.15 + (v / max) * 0.85) + ')';
								return (
									<div
										key={hr}
										title={day + ' ' + hr + ':00 — ' + v + ' call' + (v === 1 ? '' : 's')}
										style={{ flex: 1, height: '24px', backgroundColor: bg, borderRadius: '2px' }}
									/>
								);
							})}
						</div>
					))}
				</div>
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', marginTop: '8px', fontSize: '10px', color: '#888' }}>
					<span>0</span>
					<div style={{ width: '120px', height: '8px', borderRadius: '2px', background: 'linear-gradient(to right, #f0f0f0, rgba(42,106,169,0.15), #2A6AA9)' }} />
					<span>{max}</span>
				</div>
			</div>
		);
	}

	// Bookend marker — "Start" / "End" entries that bracket the timeline.
	// Visually parallel to a regular step (dot + badge + label) but with a
	// muted slate palette so they sit quietly in the margins rather than
	// competing with the action-colored flow steps in between.
	renderBookend = (kind, timestamp, callStart, mainContent) => {
		var isStart = kind === 'start';
		var color = isStart ? '#8aa0b8' : '#7a8694';
		var label = isStart ? 'Start' : 'End';
		var elapsedLabel;
		if (isStart) {
			elapsedLabel = '+0:00';
		} else if (callStart && timestamp) {
			elapsedLabel = this.elapsed(callStart, timestamp);
		} else {
			elapsedLabel = '—';
		}
		// Extra vertical gap on the inside edge of each bookend so the markers
		// visually separate from the flow steps in between. Note the end's
		// marginTop has to be larger than a step's marginBottom (14px) —
		// adjacent block margins collapse to the larger value, not the sum,
		// so 14 + 14 would still only render as 14px.
		var wrapperStyle = isStart
			? { position: 'relative', marginBottom: '28px', paddingLeft: '20px' }
			: { position: 'relative', marginTop: '28px', marginBottom: '14px', paddingLeft: '20px' };
		var bookendCardStyle = {
			display: 'flex', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap',
			backgroundColor: '#f5f8fb', border: '1px solid #e1e8f0',
			borderLeft: '3px solid ' + color, borderRadius: '4px', padding: '8px 12px'
		};
		return (
			<div style={wrapperStyle}>
				<div style={{
					position: 'absolute', left: '-19px', top: '11px',
					width: '16px', height: '16px', borderRadius: '50%',
					backgroundColor: color, border: '2px solid #fff',
					boxShadow: '0 0 0 2px ' + color
				}} />
				<div style={bookendCardStyle}>
					{/* Time column: elapsed on top, absolute below — matches step layout */}
					<div style={{ minWidth: '64px', paddingTop: '2px', fontFamily: 'monospace', lineHeight: '1.25' }}>
						<div style={{ fontSize: '11px', color: '#888' }}>
							{elapsedLabel}
						</div>
						<div style={{ fontSize: '10px', color: '#b0b8c0' }}>
							{timestamp ? this.formatTime(timestamp) : '—'}
						</div>
					</div>
					{/* Fixed-width badge slot to align with step badges */}
					<div style={{ minWidth: '120px', alignSelf: 'flex-start' }}>
						<div style={{
							display: 'inline-block',
							fontSize: '11px', fontWeight: '600', color: '#fff',
							backgroundColor: color, borderRadius: '3px',
							padding: '1px 6px', whiteSpace: 'nowrap', textAlign: 'center'
						}}>
							{label}
						</div>
					</div>
					<div style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: '600', color: color, paddingLeft: '4px', paddingRight: '8px', textAlign: 'left' }}>
						{mainContent != null ? mainContent : (timestamp ? this.formatDateTime(timestamp) : '—')}
					</div>
				</div>
			</div>
		);
	}

	// The flow-step timeline (menu moves, transfers, breadcrumbs). Every call has one:
	// for a normal IVR call it IS the detail window; for a DiVA call it sits behind the
	// 4th tab, alongside Interaction Log / Token Usage / Recording.
	renderStepTimeline = (detailData, callStart, callEnd, callID) => {
		var totalDuration = (callStart && callEnd) ? this.elapsed(callStart, callEnd) : null;

		var content;
		if (detailData && detailData.steps && detailData.steps.length > 0) {
			var steps = detailData.steps;
			content = (
				<div style={{ position: 'relative', paddingLeft: '24px', marginTop: '10px' }}>
					{/* Vertical line — centered on the dot circles (all dot centers sit at x=13 from container left) */}
					<div style={{ position: 'absolute', left: '12px', top: '0', bottom: '0', width: '2px', backgroundColor: '#d0dce8' }} />

					{this.renderBookend('start', callStart, callStart, callID ? ('Call ID: ' + callID) : null)}

					{steps.map(function (step, i) {
						var isBreadcrumb = step.action === 'Breadcrumb' || step.action === 'breadcrumb' || step.type === 'breadcrumb';
						var color = isBreadcrumb ? '#b8c2cc' : actionColor(step.action);
						var label = step.locationDesc || step.location || '—';
						var response = step.response != null ? String(step.response) : null;
						var optionDesc = step.optionDesc || null;
						var dialog = step.dialog || null;
						var detail = step.detail || step.note || null;
						var isTransfer = step.action === 'Transfer' || step.action === 'Transfers';
						var xferNum   = step.transferNumber || null;
						var xferDesc  = step.transferDescription || null;
						var xferQueue = step.transferQueue || null;

						// Breadcrumbs render in a softer, recessed style so menu-reporting
						// steps remain the visual focus of the timeline. Menu rows get a
						// card-style background that visually groups the time, badge, and
						// label into a single unit.
						var rowStyle = isBreadcrumb
							? { position: 'relative', marginBottom: '2px', paddingLeft: '20px', opacity: 0.7 }
							: { position: 'relative', marginBottom: '8px', paddingLeft: '20px' };
						var innerRowStyle = isBreadcrumb
							? { display: 'flex', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap' }
							: { display: 'flex', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap', backgroundColor: '#f5f8fb', border: '1px solid #e1e8f0', borderLeft: '3px solid ' + color, borderRadius: '4px', padding: '8px 12px' };
						var dotStyle = isBreadcrumb
							? { position: 'absolute', left: '-14px', top: '5px', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: color, border: '1px solid #fff', boxShadow: '0 0 0 1px ' + color }
							: { position: 'absolute', left: '-17px', top: '13px', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: color, border: '2px solid #fff', boxShadow: '0 0 0 2px ' + color };
						var labelStyle = isBreadcrumb
							? { fontSize: '12px', fontWeight: 'normal', color: '#7a8694', fontStyle: 'italic' }
							: { fontSize: '13px', fontWeight: 'bold', color: '#2A6AA9' };
						// Fixed-width badge slot so menu labels and breadcrumb labels line up vertically.
						var BADGE_SLOT_WIDTH = '120px';
						var badgeStyle = { fontSize: '11px', fontWeight: 'bold', color: '#fff', backgroundColor: color, borderRadius: '3px', padding: '1px 6px', whiteSpace: 'nowrap', textAlign: 'center', alignSelf: 'flex-start', display: 'inline-block' };
						var hasResponse = response != null && response !== '';
						var hasOption = !!optionDesc;

						return (
							<div key={step.id || i} style={rowStyle}>
								{/* Circle dot */}
								<div style={dotStyle} />

								<div style={innerRowStyle}>
									{/* Time column: elapsed on top, absolute below — blank placeholder for breadcrumbs so labels align */}
									<div style={{ minWidth: '64px', paddingTop: '2px', fontFamily: 'monospace', lineHeight: '1.25' }}>
										{!isBreadcrumb && (
											<React.Fragment>
												<div style={{ fontSize: '11px', color: '#888' }}>
													{callStart ? this.elapsed(callStart, step.dateTime) : this.formatTime(step.dateTime)}
												</div>
												<div style={{ fontSize: '10px', color: '#b0b8c0' }}>
													{this.formatTime(step.dateTime)}
												</div>
											</React.Fragment>
										)}
									</div>

									{/* Badge slot — fixed width so menu labels and breadcrumb labels line up.
									    Menu steps: colored badge.
									    Breadcrumbs: a thin dashed line that connects the timeline dot to the text. */}
									{!isBreadcrumb ? (
										<div style={{ minWidth: BADGE_SLOT_WIDTH, alignSelf: 'flex-start' }}>
											<div style={badgeStyle}>
												{step.action || '?'}
											</div>
										</div>
									) : (
										<div style={{ minWidth: BADGE_SLOT_WIDTH, alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}>
											<div style={{ flex: 1, borderTop: '1px dashed #c8d1dc' }} />
										</div>
									)}

									{/* Content */}
									<div style={{ flex: 1, minWidth: 0, paddingLeft: '4px', paddingRight: '8px', textAlign: 'left' }}>
										<div style={labelStyle}>{label}</div>
										{dialog && (
											<div style={{ fontSize: '12px', color: '#555', fontStyle: 'italic', marginTop: '1px' }}>
												"{dialog}"
											</div>
										)}
										{detail && (
											<div style={{ fontSize: '11px', color: '#8a95a3', marginTop: '1px' }}>
												{detail}
											</div>
										)}
										{/* Transfer destination — only shown for Transfer steps */}
										{isTransfer && (xferNum || xferDesc || xferQueue) && (
											<div style={{ fontSize: '12px', color: '#27a060', marginTop: '2px', fontWeight: '600' }}>
												→ {xferNum ? this.formatPhone(xferNum) : ''}
												{xferDesc ? (xferNum ? ' — ' : '') + xferDesc : ''}
												{xferQueue ? ((xferNum || xferDesc) ? ' · ' : '') + 'Queue: ' + xferQueue : ''}
											</div>
										)}
										{(hasResponse || hasOption) && (
											<div style={{ fontSize: '12px', color: '#333', marginTop: '2px' }}>
												{hasResponse && (
													<span style={{ backgroundColor: '#eef3f9', border: '1px solid #c0d0e0', borderRadius: '3px', padding: '1px 6px', fontFamily: 'monospace', marginRight: '6px' }}>
														{response}
													</span>
												)}
												{hasOption && <span style={{ color: '#555' }}>{optionDesc}</span>}
											</div>
										)}
									</div>
								</div>
							</div>
						);
					}.bind(this))}

					{this.renderBookend('end', callEnd, callStart, totalDuration ? ('Total Duration: ' + totalDuration) : null)}
				</div>
			);
		} else {
			content = <div style={{ color: '#888', padding: '20px 0', fontSize: '13px' }}>No flow steps recorded for this call.</div>;
		}
		return content;
	}

	// Stable color per sub-bot for the Interaction Log. Assigned by the bot's position
	// in this call's distinct-bot list, so a call always paints the same regardless of
	// which chatbot it is (sub-bots carry no color of their own in the config).
	botColor = (botId, order) => {
		var i = order.indexOf(botId);
		if (i < 0) i = 0;
		return DIVA_BOT_COLORS[i % DIVA_BOT_COLORS.length];
	}

	// Tab 1 — the conversation, laid out like a chat: caller bubbles on the left,
	// bot bubbles on the right.
	//
	// Turns are grouped into SEGMENTS of consecutive turns handled by the same sub-bot,
	// and each segment is one unbroken tinted block — so a stretch of conversation with
	// one bot reads as a single continuous section rather than a stack of cards. A bot
	// change breaks the segment and gets an explicit hand-off marker between them.
	//
	// Note a bot that comes back later (A -> B -> A) correctly opens a THIRD segment:
	// segments follow contiguous runs, while COLOR follows the bot itself (via `order`),
	// so A's two segments share A's color.
	// Post-call summary & sentiment (worker-written connectionMeta 42/43/44/45). Works for any
	// recorded call the number opted in for, not just DiVA.
	renderSummary = (detailData) => {
		var s = detailData && detailData.sentiment;
		var msg = function (color, text) { return <div style={{ color: color, padding: '20px 0', fontSize: '13px' }}>{text}</div>; };
		if (!s) return msg('#888', 'No analysis for this call.');
		if (s.status === '0') return msg('#888', 'Sentiment analysis is pending — it runs shortly after the call ends and its recording finalizes. Check back in a few minutes.');
		if (s.status === '9' && !s.summary) return msg('#c0392b', 'Sentiment analysis could not be completed for this call (the recording may be missing or unreadable).');

		var score = (typeof s.score === 'number' && isFinite(s.score)) ? s.score : null;   // -100..100
		var label = s.label || 'neutral';
		var col = label === 'positive' ? '#1f8a4c' : (label === 'negative' ? '#c0392b' : '#7a8699');
		var pct = score != null ? Math.max(0, Math.min(100, Math.round((score + 100) / 2))) : 50;
		var labelHdr = { fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.7px', color: '#8a93a0', marginBottom: '4px' };

		return (
			<div style={{ marginTop: '10px' }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px' }}>
					<span style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.7px', color: col, border: '2px solid ' + col, borderRadius: '999px', padding: '4px 14px' }}>{label}</span>
					{score != null && <span style={{ fontSize: '13px', color: '#4a5560', flexShrink: 0 }}>score {score > 0 ? '+' + score : score}</span>}
					<div style={{ flex: 1, height: '8px', borderRadius: '999px', background: 'linear-gradient(90deg,#c0392b,#e6e9ef 50%,#1f8a4c)', position: 'relative', minWidth: '80px' }}>
						<div style={{ position: 'absolute', left: 'calc(' + pct + '% - 6px)', top: '-2px', width: '12px', height: '12px', borderRadius: '50%', background: '#fff', border: '2px solid ' + col }} />
					</div>
				</div>

				{s.summary && (
					<div style={{ marginBottom: '16px' }}>
						<div style={labelHdr}>Summary</div>
						<div style={{ fontSize: '14px', lineHeight: '1.5', color: '#222', whiteSpace: 'pre-wrap' }}>{s.summary}</div>
					</div>
				)}
				{s.tone && (
					<div style={{ marginBottom: '16px' }}>
						<div style={labelHdr}>Caller tone</div>
						<div style={{ fontSize: '14px', color: '#222' }}>{s.tone}</div>
					</div>
				)}
				{s.topics && s.topics.length > 0 && (
					<div style={{ marginBottom: '16px' }}>
						<div style={labelHdr}>Topics</div>
						<div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
							{s.topics.map(function (t, i) { return <span key={i} style={{ fontSize: '12px', background: '#eef2f7', color: '#37475a', borderRadius: '999px', padding: '4px 12px' }}>{String(t)}</span>; })}
						</div>
					</div>
				)}
				{(s.followUpNeeded || s.escalated) && (
					<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
						{s.followUpNeeded && <span style={{ fontSize: '12px', fontWeight: '600', color: '#b9770e', background: '#fdf3e2', borderRadius: '6px', padding: '5px 12px' }}>Follow-up needed</span>}
						{s.escalated && <span style={{ fontSize: '12px', fontWeight: '600', color: '#2A6AA9', background: '#e8f0fa', borderRadius: '6px', padding: '5px 12px' }}>Escalated to a human</span>}
					</div>
				)}
			</div>
		);
	}

	renderInteractionLog = (detailData) => {
		var turns = (detailData && detailData.transcript) || [];
		if (!turns.length) {
			return (
				<div style={{ color: '#888', padding: '20px 0', fontSize: '13px' }}>
					No conversation was logged for this call.
				</div>
			);
		}
		// Distinct sub-bots in first-seen order — drives color assignment.
		var order = [];
		turns.forEach(function (t) { if (t.botId && order.indexOf(t.botId) < 0) order.push(t.botId); });

		// Collapse consecutive same-bot turns into segments.
		var segments = [];
		turns.forEach(function (t) {
			var last = segments[segments.length - 1];
			if (last && last.botId === t.botId) last.turns.push(t);
			else segments.push({ botId: t.botId, botName: t.botName || t.botId || 'Assistant', turns: [t] });
		});

		var labelStyle = { fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '3px' };
		var textStyle = { fontSize: '13px', lineHeight: '1.45', whiteSpace: 'pre-wrap', wordBreak: 'break-word' };

		return (
			<div style={{ marginTop: '10px' }}>
				{segments.map((seg, si) => {
					var c = this.botColor(seg.botId, order);
					return (
						<React.Fragment key={si}>
							{/* Hand-off marker — the moment the caller was passed to a different bot */}
							{si > 0 && (
								<div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '16px 0' }}>
									<div style={{ flex: 1, height: '1px', backgroundColor: '#d8e2ec' }} />
									<div style={{ display: 'flex', alignItems: 'center', gap: '7px', backgroundColor: '#fff', border: '1px solid ' + c.accent, borderRadius: '13px', padding: '5px 13px', whiteSpace: 'nowrap' }}>
										<span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: c.accent, flexShrink: 0 }} />
										<span style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#4a5560' }}>
											Handed off to {seg.botName}
										</span>
									</div>
									<div style={{ flex: 1, height: '1px', backgroundColor: '#d8e2ec' }} />
								</div>
							)}

							{/* One unbroken tint for everything this bot handled */}
							<div style={{ backgroundColor: c.bg, borderLeft: '4px solid ' + c.accent, borderRadius: '6px', padding: '12px 14px', marginBottom: '8px' }}>
								<div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px' }}>
									<span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: c.accent, flexShrink: 0 }} />
									<span style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', color: c.accent }}>
										{seg.botName}
									</span>
								</div>

								{seg.turns.map((t, ti) => (
									<React.Fragment key={t.id || ti}>
										{/* Caller — left */}
										{t.caller ? (
											<div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '6px' }}>
												<div style={{ maxWidth: '78%', backgroundColor: '#ffffff', border: '1px solid #dbe3ea', borderRadius: '12px 12px 12px 3px', padding: '8px 11px' }}>
													<div style={Object.assign({}, labelStyle, { color: '#8a95a3' })}>Caller</div>
													<div style={Object.assign({}, textStyle, { color: '#222' })}>{t.caller}</div>
												</div>
											</div>
										) : null}
										{/* Bot — right, in the segment's color */}
										{t.bot ? (
											<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
												<div style={{ maxWidth: '78%', backgroundColor: c.bubble, borderRadius: '12px 12px 3px 12px', padding: '8px 11px' }}>
													<div style={Object.assign({}, labelStyle, { color: c.accent, textAlign: 'right' })}>{seg.botName}</div>
													<div style={Object.assign({}, textStyle, { color: '#1e2a35' })}>{t.bot}</div>
													<div style={{ fontSize: '9px', color: '#7d8894', fontFamily: 'monospace', textAlign: 'right', marginTop: '4px' }}>
														{this.formatTime(t.dateTime)}
													</div>
												</div>
											</div>
										) : null}
									</React.Fragment>
								))}
							</div>
						</React.Fragment>
					);
				})}
			</div>
		);
	}

	// Tab 2 — token usage for the whole call, grouped by model. Each model row expands
	// to the per-stage split the token flow already records. The raw stage values are
	// diva-ai's internals (dispatch/answer/assess); STAGE_LABELS below maps them to what
	// the tokens were actually spent ON, which is the question this tab answers.
	renderTokenUsage = (detailData) => {
		var rows = (detailData && detailData.tokens) || [];
		if (!rows.length) {
			return (
				<div style={{ color: '#888', padding: '20px 0', fontSize: '13px' }}>
					No token usage was logged for this call.
				</div>
			);
		}
		// Keys are diva-ai's stage strings (twilio.js tokenFlow) — don't rename those
		// without updating the emitter, or rows fall back to showing the raw value.
		var STAGE_LABELS = { dispatch: 'Routing', answer: 'Conversation', assess: 'Failure check' };
		// Fold the per-(model, stage) rows the server returns up into per-model totals,
		// keeping the stage rows as children.
		var byModel = {};
		var modelOrder = [];
		rows.forEach(function (r) {
			if (!byModel[r.model]) { byModel[r.model] = { model: r.model, input: 0, output: 0, calls: 0, stages: [] }; modelOrder.push(r.model); }
			var m = byModel[r.model];
			m.input += r.input; m.output += r.output; m.calls += r.calls;
			m.stages.push(r);
		});
		var total = rows.reduce(function (a, r) { return { input: a.input + r.input, output: a.output + r.output, calls: a.calls + r.calls }; }, { input: 0, output: 0, calls: 0 });
		var expanded = this.state.expandedTokenModels || {};
		var num = function (n) { return Number(n || 0).toLocaleString(); };
		var cell = { padding: '7px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
		var head = { padding: '7px 10px', textAlign: 'right', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#8a95a3', fontWeight: '700', borderBottom: '1px solid #e0e0e0' };

		return (
			<div style={{ marginTop: '10px' }}>
				{/* Headline totals for the call */}
				<div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
					{[{ label: 'Input tokens', value: total.input }, { label: 'Output tokens', value: total.output }, { label: 'Total tokens', value: total.input + total.output }].map((k) => (
						<div key={k.label} style={{ flex: 1, background: '#f5f8fb', border: '1px solid #e1e8f0', borderRadius: '4px', padding: '10px 12px' }}>
							<div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#8a95a3', fontWeight: '700' }}>{k.label}</div>
							<div style={{ fontSize: '20px', fontWeight: '700', color: '#002f5f', fontVariantNumeric: 'tabular-nums' }}>{num(k.value)}</div>
						</div>
					))}
				</div>

				<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
					<thead>
						<tr>
							<th style={Object.assign({}, head, { textAlign: 'left' })}>Model</th>
							<th style={head}>Calls</th>
							<th style={head}>Input</th>
							<th style={head}>Output</th>
							<th style={head}>Total</th>
						</tr>
					</thead>
					<tbody>
						{modelOrder.map((name) => {
							var m = byModel[name];
							var open = !!expanded[name];
							return (
								<React.Fragment key={name}>
									<tr
										style={{ cursor: 'pointer', backgroundColor: '#fff', borderBottom: '1px solid #eef2f6' }}
										onClick={() => this.setState({ expandedTokenModels: Object.assign({}, expanded, { [name]: !open }) })}
									>
										<td style={{ padding: '7px 10px', textAlign: 'left', fontWeight: '600', color: '#2A6AA9' }}>
											<span style={{ display: 'inline-block', width: '12px', color: '#8a95a3' }}>{open ? '▾' : '▸'}</span>
											{name}
										</td>
										<td style={cell}>{num(m.calls)}</td>
										<td style={cell}>{num(m.input)}</td>
										<td style={cell}>{num(m.output)}</td>
										<td style={Object.assign({}, cell, { fontWeight: '600' })}>{num(m.input + m.output)}</td>
									</tr>
									{open && m.stages.map((s) => (
										<tr key={name + ':' + s.stage} style={{ backgroundColor: '#f9fbfd', color: '#555' }}>
											<td style={{ padding: '5px 10px 5px 32px', textAlign: 'left', fontSize: '12px' }}>{STAGE_LABELS[s.stage] || s.stage}</td>
											<td style={Object.assign({}, cell, { fontSize: '12px' })}>{num(s.calls)}</td>
											<td style={Object.assign({}, cell, { fontSize: '12px' })}>{num(s.input)}</td>
											<td style={Object.assign({}, cell, { fontSize: '12px' })}>{num(s.output)}</td>
											<td style={Object.assign({}, cell, { fontSize: '12px' })}>{num(s.input + s.output)}</td>
										</tr>
									))}
								</React.Fragment>
							);
						})}
						<tr style={{ borderTop: '2px solid #d8e2ec', fontWeight: '700', color: '#002f5f' }}>
							<td style={{ padding: '7px 10px', textAlign: 'left' }}>All models</td>
							<td style={cell}>{num(total.calls)}</td>
							<td style={cell}>{num(total.input)}</td>
							<td style={cell}>{num(total.output)}</td>
							<td style={cell}>{num(total.input + total.output)}</td>
						</tr>
					</tbody>
				</table>
			</div>
		);
	}

	// The recording subheader — a band under the navy header, above the tabs. Sits there
	// rather than being a tab so it stays on screen whichever tab you're reading, and so
	// it works for IVR calls, which have no tab bar at all.
	//
	// Shown for ANY call type that actually has a recording. We can't know that from the
	// connectionMeta 40 flag alone: phone-router only sets it on chatbot calls, and legacy
	// IVR calls never pass through it. So the lookup runs for every call with a Twilio
	// CallSid, and Twilio's answer is what decides. The flag is still used — it's the
	// difference between "expected a recording, still waiting" and "never recorded, say
	// nothing".
	//
	// The player is custom: the <audio> element carries no `controls` and is never shown,
	// so we get the app's own look instead of the browser's default chrome (which differs
	// between Chrome and Firefox and matches nothing else here). State is mirrored off the
	// element's events, so scrubbing/keyboard/ended all stay in sync.
	renderRecordingBar = (detailData) => {
		if (!detailData) {
			return null;
		}
		var uri = detailData.recording;
		var error = detailData.recordingError;

		// Nothing to say for a call that was never recorded: no flag, no uri => render
		// nothing at all. Most IVR calls land here.
		if (!uri && !detailData.recorded) return null;

		// Lookup finished and Twilio has no recording — render nothing (not an error message).
		if (!uri && detailData.recordingMissing) return null;

		// Flagged as recorded but no uri yet — say why rather than showing a dead bar.
		if (!uri) {
			return (
				<div style={recordingBarStyle}>
					<span style={{ fontSize: '12px', color: error ? '#903' : '#7d8894' }}>
						{error || 'Loading recording…'}
					</span>
				</div>
			);
		}

		// Twilio hands back an API uri ending in .json; strip it for the media URL.
		var media = String(uri).indexOf('http') === 0 ? String(uri) : ('https://api.twilio.com' + String(uri).replace('.json', ''));
		var proxied = String(uri).indexOf('http') === 0 ? String(uri) : (window.location.origin + '/recording' + String(uri).replace('.json', ''));
		var playing = this.state.audioPlaying;
		var dur = this.state.audioDuration;
		var pct = dur ? Math.max(0, Math.min(100, (this.state.audioTime / dur) * 100)) : 0;
		var timeStyle = { fontSize: '11px', fontFamily: 'monospace', color: '#6b7885', fontVariantNumeric: 'tabular-nums', flexShrink: 0 };

		return (
			<div style={recordingBarStyle}>
				<audio
					ref={this.audioRef}
					src={media}
					preload="metadata"
					style={{ display: 'none' }}
					onLoadedMetadata={(e) => this.setState({ audioDuration: e.currentTarget.duration || 0 })}
					onTimeUpdate={(e) => this.setState({ audioTime: e.currentTarget.currentTime || 0 })}
					onPlay={() => this.setState({ audioPlaying: true })}
					onPause={() => this.setState({ audioPlaying: false })}
					onEnded={() => this.setState({ audioPlaying: false, audioTime: 0 })}
					onError={() => this.setState({ audioPlaying: false })}
				/>

				<div
					onClick={this.toggleRecording}
					title={playing ? 'Pause' : 'Play'}
					style={{
						width: '30px', height: '30px', borderRadius: '50%', backgroundColor: '#2A6AA9',
						color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
						cursor: 'pointer', flexShrink: 0, paddingLeft: playing ? '0' : '2px'
					}}
				>
					{/* Inline SVG rather than a glyph font or unicode — crisp, and renders
					    identically in every browser. */}
					{playing ? (
						<svg width="9" height="11" viewBox="0 0 9 11" aria-hidden="true">
							<rect x="0" y="0" width="3" height="11" fill="currentColor" />
							<rect x="6" y="0" width="3" height="11" fill="currentColor" />
						</svg>
					) : (
						<svg width="10" height="11" viewBox="0 0 10 11" aria-hidden="true">
							<path d="M0 0 L10 5.5 L0 11 Z" fill="currentColor" />
						</svg>
					)}
				</div>

				<span style={timeStyle}>{this.formatAudioTime(this.state.audioTime)}</span>

				<div
					onClick={this.seekRecording}
					style={{ flex: 1, minWidth: '60px', height: '14px', display: 'flex', alignItems: 'center', cursor: dur ? 'pointer' : 'default' }}
				>
					<div style={{ position: 'relative', width: '100%', height: '5px', borderRadius: '3px', backgroundColor: '#c9d7e5' }}>
						<div style={{ width: pct + '%', height: '100%', borderRadius: '3px', backgroundColor: '#2A6AA9' }} />
						{/* Playhead knob — nudged back by half its width so it centers on the fill edge */}
						<div style={{ position: 'absolute', top: '50%', left: pct + '%', width: '11px', height: '11px', borderRadius: '50%', backgroundColor: '#2A6AA9', border: '2px solid #fff', transform: 'translate(-50%, -50%)', boxShadow: '0 1px 2px rgba(0,0,0,0.25)', pointerEvents: 'none' }} />
					</div>
				</div>

				<span style={timeStyle}>{this.formatAudioTime(dur)}</span>

				<a
					style={{ flexShrink: 0, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#2A6AA9', textDecoration: 'none', whiteSpace: 'nowrap' }}
					href={proxied}
					download={(this.state.activeCallPhone || 'call') + '.wav'}
				>Download</a>
			</div>
		);
	}

	// The call-detail window. DiVA calls (connection.type 8) get the tabbed view;
	// every other call type opens straight onto the step timeline exactly as before.
	renderCallDetail = () => {
		var detailData = this.props.data ? this.props.data.advancedCallDetailData : null;
		var detailLoading = this.props.data ? this.props.data.advancedCallDetailLoading : false;
		// Prefer server-supplied start (authoritative) but fall back to the
		// timestamp passed in from the report row click.
		var callStart = (detailData && detailData.callStart) || this.state.activeCallStart;
		var callEnd   = detailData && detailData.callEnd;
		var callID    = (detailData && detailData.callID) || this.state.activeCallID;
		var isDiva    = !!(detailData && detailData.callType === DIVA_MEDIA_TYPE);
		var tab       = this.state.activeCallTab || 'interaction';

		// Post-call sentiment/summary (worker-written) applies to ALL recorded calls, not just
		// DiVA. DiVA gets its transcript/token/timeline tabs; ANY call with sentiment also gets a
		// Summary tab (and a non-DiVA call gets a Timeline tab to sit beside it). effTab falls
		// back to the first offered tab when the stored one isn't available for this call.
		var hasSentiment = !!(detailData && detailData.sentiment);
		var tabs = [];
		if (isDiva) {
			tabs.push({ key: 'interaction', label: 'Interaction Log' });
			tabs.push({ key: 'tokens', label: 'Token Usage' });
			tabs.push({ key: 'timeline', label: 'Timeline' });
		} else if (hasSentiment) {
			tabs.push({ key: 'timeline', label: 'Timeline' });
		}
		if (hasSentiment) tabs.push({ key: 'summary', label: 'Summary & Sentiment' });
		var effTab = tabs.some(function (t) { return t.key === tab; }) ? tab : (tabs[0] ? tabs[0].key : tab);

		var content;
		if (detailLoading) {
			content = (
				<div style={{ position: 'static', height: '100px' }}>
					<div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
						<div style={{ margin: '60px' }} className="loader" />
					</div>
				</div>
			);
		} else if (!detailData) {
			content = null;
		} else if (effTab === 'summary' && hasSentiment) {
			content = this.renderSummary(detailData);
		} else if (!isDiva) {
			content = this.renderStepTimeline(detailData, callStart, callEnd, callID);
		} else if (effTab === 'interaction') {
			content = this.renderInteractionLog(detailData);
		} else if (effTab === 'tokens') {
			content = this.renderTokenUsage(detailData);
		} else {
			content = this.renderStepTimeline(detailData, callStart, callEnd, callID);
		}

		// Recording is deliberately NOT a tab — it renders above them (renderRecordingBar), so it
		// stays visible on every tab and works for IVR calls that have no tab bar.

		// Flex column layout — header pinned, body fills the rest and scrolls.
		// Previously the body had its own maxHeight (65vh) larger than the .pop
		// max-height (60vh from CSS), which meant the parent clipped the body
		// before its overflow:auto could engage, hiding the scrollbar when
		// content was tall. Letting the body grow inside an 85vh-capped flex
		// column makes the scroll reliable.
		var popStyle = {
			maxWidth: '700px',
			width: '92vw',
			maxHeight: '85vh',
			padding: '0',
			overflow: 'hidden',
			display: 'flex',
			flexDirection: 'column'
		};
		return (
			<div className="blackout">
				<div className="pop" style={popStyle}>
					<div className="viewCloser" style={{ display: 'block' }} onClick={this.closeCallDetail} />
					<div style={{ background: '#002f5f', padding: '24px 32px', color: '#fff', flexShrink: 0 }}>
						<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>
							{this.formatDateTime(this.state.activeCallStart)}
							{isDiva && <span style={{ marginLeft: '8px', color: 'rgba(255,255,255,0.85)' }}>· DiVA</span>}
						</div>
						<div style={{ fontSize: '22px', fontWeight: '700' }}>
							{this.formatPhone(this.state.activeCallPhone)}
						</div>
					</div>
					{!detailLoading && this.renderRecordingBar(detailData)}
					{tabs.length > 0 && !detailLoading && detailData && (
						<div style={{ display: 'flex', justifyContent: 'center', borderBottom: '1px solid #e1e8f0', flexShrink: 0, background: '#fff' }}>
							{tabs.map((t) => {
								var on = effTab === t.key;
								return (
									<div
										key={t.key}
										onClick={() => this.selectCallTab(t.key)}
										style={{
											padding: '10px 16px', cursor: 'pointer', fontSize: '12px', fontWeight: '700',
											textTransform: 'uppercase', letterSpacing: '0.6px',
											color: on ? '#2A6AA9' : '#8a95a3',
											borderBottom: on ? '2px solid #2A6AA9' : '2px solid transparent'
										}}
									>{t.label}</div>
								);
							})}
						</div>
					)}
					<div style={{ padding: '16px 24px 24px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
						{content}
					</div>
				</div>
			</div>
		);
	}

	render() {
		if (!this.props.active) return null;

		var reportData = this.props.data ? this.props.data.advancedReportData : null;
		var loading = this.props.data ? this.props.data.advancedReportLoading : false;

		var reportList = (this.props.data && this.props.data.AdvancedReports && this.props.data.AdvancedReports.reportList) || [];
		var reportRows = reportList.map((rep) => (
			<tr
				key={rep.route}
				className="dataRow"
				style={{ cursor: 'pointer', justifyContent: 'flex-start' }}
				onClick={() => this.selectReport(rep)}
				onMouseEnter={() => this.setState({ hoveredReportRow: rep.route })}
				onMouseLeave={() => this.setState({ hoveredReportRow: null })}
			>
				<td style={{ width: '270px', flexShrink: 0, textAlign: 'left', fontWeight: '600' }}>{rep.name}</td>
				<td style={{ flex: 1, textAlign: 'left', color: this.state.hoveredReportRow === rep.route ? '#fff' : '#666', fontSize: '13px', lineHeight: '1.4', display: 'flex', alignItems: 'center', paddingTop: '4px' }}>{rep.description || ''}</td>
				<td style={{ width: '130px', flexShrink: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingRight: '6px' }}>
					<div
						onClick={this.openSchedule(rep)}
						style={{
							padding: '6px 14px',
							fontSize: '13px',
							fontWeight: '600',
							lineHeight: '1',
							color: '#2A6AA9',
							background: '#f0f4f8',
							border: '1px solid #d3dde7',
							borderRadius: '4px',
							cursor: 'pointer',
							letterSpacing: '0.3px',
							whiteSpace: 'nowrap'
						}}
						onMouseEnter={(e) => { e.currentTarget.style.background = '#e3ebf3'; }}
						onMouseLeave={(e) => { e.currentTarget.style.background = '#f0f4f8'; }}
					>
						Schedule
					</div>
				</td>
			</tr>
		));

		var modal = null;
		if (this.state.activeReport) {
			var rep = this.state.activeReport;

			// `advancedReportData` lives in App.js state and only gets cleared when
			// the next report's response arrives — so when switching reports we may
			// still be holding the previous report's data. Gate on the report's
			// route (stable id) when the server emits one; fall back to title so
			// older responses still match.
			if (reportData) {
				var mismatch = reportData.route
					? reportData.route !== rep.route
					: reportData.title !== rep.name;
				if (mismatch) reportData = null;
			}

			// Each parameter renders as a single flex column with label-on-top, input
			// below. Bundling them into one flex item keeps the label aligned over
			// its input regardless of input width drift (Flatpickr wrappers, etc).
			var paramFields = rep.parameters.map((p) => (
				<div key={p.parameter} style={{ display: 'flex', flexDirection: 'column', flex: '1 1 100px', minWidth: '50px' }}>
					<div style={{ textAlign: 'center', padding: '0 4px 5px', fontSize: '14px', color: '#555' }}>{p.label}</div>
					{this.renderParamInput(p)}
				</div>
			));

			// Application selector — pulled live from the logger DB. Only shown when there's
			// more than one application; with a single application it's auto-selected (via
			// effectiveApplication) and the dropdown is skipped entirely.
			var reportApps = this.reportApps();
			var applicationField = null;
			if (reportApps.length > 1) {
				var selApp = this.effectiveApplication();
				applicationField = (
					<div key="application" style={{ display: 'flex', flexDirection: 'column', flex: '1 1 100px', minWidth: '50px' }}>
						<div style={{ textAlign: 'center', padding: '0 4px 5px', fontSize: '14px', color: '#555' }}>Application</div>
						<select
							value={selApp != null ? selApp : ''}
							onChange={(e) => this.setState({ application: e.target.value }, this.rerunIfShowing)}
							style={{ height: '60px', fontSize: '20px', padding: '0 12px', width: '100%', boxSizing: 'border-box' }}
						>
							{reportApps.map((a) => (
								<option key={a.id} value={a.id}>{a.name || ('Application ' + a.id)}</option>
							))}
						</select>
					</div>
				);
			}

			// Visualization (summary only — dropdown to toggle between chart types)
			var visualization = null;
			var summary = !!(reportData && reportData.summary);
			if (summary && reportData) {
				// Reports can restrict which chart types make sense for them. Default
				// to all three (Interaction Summary).
				var availableTypes = (reportData.chartTypes && reportData.chartTypes.length > 0)
					? reportData.chartTypes
					: ['bar', 'pie', 'heatmap'];

				var hasChartData = reportData.chart && reportData.chart.data && reportData.chart.data.length > 0;
				var g = hasChartData ? reportData.chart.granularity : null;
				var granLabel = g ? (GRAN_LABELS[g] || (g.charAt(0).toUpperCase() + g.slice(1))) : '';
				var chartTitle = (reportData.chart && reportData.chart.title) || ('Calls per ' + granLabel);

				// Drilldown — Menu Option Summary lets the user click a menu bar to
				// switch to a bar chart of that menu's options.
				var optionsByMenu = reportData.optionsByMenu || null;
				var drilledMenu = this.state.drilldownMenu;
				var inDrilldown = !!(optionsByMenu && drilledMenu && optionsByMenu[drilledMenu]);

				var displayedChartData = inDrilldown ? optionsByMenu[drilledMenu] : (reportData.chart && reportData.chart.data) || [];
				var drilldownTpl = (reportData.chart && reportData.chart.drilldownTitle) || 'Options in {key}';
				var displayedChartTitle = inDrilldown ? drilldownTpl.replace('{key}', drilledMenu) : chartTitle;
				var barLabel = (reportData.chart && reportData.chart.barLabel) || 'Calls';
				var canDrilldown = !!optionsByMenu && !inDrilldown;
				// Server can opt bars into "click to append the bar's label to a
				// filter array and re-run" — used by Breadcrumb Summary's cohort
				// chips.
				var chartAppendParam = (reportData.chart && reportData.chart.appendFilterOnClick) || null;
				var canAppendFromChart = !!chartAppendParam && !inDrilldown;
				// Chart-level click catches anywhere inside the highlighted column
				// (above the bar, on the x-axis tick) — not just on the colored
				// bar itself. recharts passes the column's x-axis value as
				// `activeLabel`, which we use as the drilldown / cohort key.
				var chartOnClick = canDrilldown
					? (e) => { if (e && e.activeLabel != null) this.setState({ drilldownMenu: e.activeLabel }); }
					: (canAppendFromChart
						? (e) => { if (e && e.activeLabel != null) this.appendFilterValue(chartAppendParam, e.activeLabel); }
						: undefined);

				var bar = null;
				if (availableTypes.indexOf('bar') !== -1 && (hasChartData || inDrilldown)) {
					bar = (
						<div style={{ marginBottom: '8px' }}>
							<div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '6px' }}>
								<div style={{ fontSize: '15px', fontWeight: 'bold', color: '#2A6AA9' }}>
									{displayedChartTitle}
								</div>
								{inDrilldown && (
									<div
										onClick={() => this.setState({ drilldownMenu: null })}
										style={{ fontSize: '12px', color: '#2A6AA9', cursor: 'pointer', textDecoration: 'underline' }}
									>
										← Back
									</div>
								)}
							</div>
							{canDrilldown && (
								<div data-pdf-hide="true" style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Tip: click a bar to see its breakdown.</div>
							)}
							{canAppendFromChart && (
								<div data-pdf-hide="true" style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Tip: click a bar to limit the report to callers who hit it.</div>
							)}
							<div style={{ cursor: (canDrilldown || canAppendFromChart) ? 'pointer' : 'default' }}>
								<ResponsiveContainer width="100%" height={220}>
									<BarChart data={displayedChartData} margin={{ top: 8, right: 20, left: 0, bottom: 35 }} onClick={chartOnClick}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="period" angle={-45} textAnchor="end" interval="preserveStartEnd" tick={{ fontSize: 10 }} height={50} />
										<YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={40} />
										<Tooltip formatter={(value) => [value, barLabel]} />
										<Bar dataKey="count" fill="#2A6AA9" name={barLabel} radius={[2, 2, 0, 0]} />
									</BarChart>
								</ResponsiveContainer>
							</div>
						</div>
					);
				}

				// Pie: share of calls per group. With many slices the chart becomes
				// unreadable, so roll long tails (>8 entries) into "Other".
				var pie = null;
				if (availableTypes.indexOf('pie') !== -1 && hasChartData) {
					// Pie excludes the "All matching callers" total (it's the
					// universe, not a slice) but KEEPS "Didn't hit any shown
					// breadcrumb" so slices reflect the full cohort split.
					var pieData = (reportData.chart.data || []).filter(function (d) { return d.kind !== 'context-total'; });
					if (pieData.length > 8) {
						var sorted = pieData.slice().sort(function (a, b) { return b.count - a.count; });
						var top = sorted.slice(0, 7);
						var rest = sorted.slice(7);
						var otherTotal = rest.reduce(function (s, d) { return s + (d.count || 0); }, 0);
						pieData = top.concat([{ period: 'Other', count: otherTotal }]);
					}
					var totalCalls = pieData.reduce(function (s, d) { return s + (d.count || 0); }, 0) || 1;
					pie = (
						<div style={{ marginBottom: '20px' }}>
							<div style={{ fontSize: '15px', fontWeight: 'bold', color: '#2A6AA9', marginBottom: '6px' }}>
								{(reportData.chart && reportData.chart.title) ? ('Share — ' + chartTitle) : ('Share of Calls per ' + granLabel)}
							</div>
							<ResponsiveContainer width="100%" height={300}>
								<PieChart>
									<Pie
										data={pieData}
										dataKey="count"
										nameKey="period"
										outerRadius={110}
										label={(d) => d.period + ' (' + Math.round((d.count / totalCalls) * 100) + '%)'}
										labelLine={false}
									>
										{pieData.map((entry, i) => (
											<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
										))}
									</Pie>
									<Tooltip formatter={(value, name) => [value + ' (' + Math.round((value / totalCalls) * 100) + '%)', name]} />
								</PieChart>
							</ResponsiveContainer>
						</div>
					);
				}

				var heat = (availableTypes.indexOf('heatmap') !== -1 && reportData.heatmap)
					? this.renderHeatmap(reportData.heatmap)
					: null;

				// Horizontal bar with a fixed 0–100% x-axis. Reads "% of calls"
				// at a glance and accommodates long category labels. Requires
				// chart.denominator from the server to compute the percentage.
				var hbar = null;
				if (availableTypes.indexOf('hbar') !== -1 && (hasChartData || inDrilldown)) {
					var hbarDenom = (reportData.chart && reportData.chart.denominator) || 0;
					// Drop the "All matching callers" total — it's always 100% and
					// adds no information to the bar chart. Keep "Didn't hit any
					// shown" (context-remainder) so its share is visible against
					// the breakdown bars. The table still shows both rows.
					var hbarData = displayedChartData
						.filter(function (d) { return d.kind !== 'context-total'; })
						.map(function (d) {
							return {
								period: d.period,
								count: d.count,
								pct: d.pct != null ? d.pct : (hbarDenom > 0 ? (d.count / hbarDenom) * 100 : 0),
								kind: d.kind || 'data'
							};
						});
					var maxLabelLen = hbarData.reduce(function (m, d) { return Math.max(m, (d.period || '').length); }, 0);
					var yAxisWidth = Math.min(220, Math.max(80, maxLabelLen * 7));
					var hbarHeight = Math.max(220, hbarData.length * 28 + 60);
					hbar = (
						<div style={{ marginBottom: '8px' }}>
							<div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '6px' }}>
								<div style={{ fontSize: '15px', fontWeight: 'bold', color: '#2A6AA9' }}>
									{displayedChartTitle} <span style={{ fontWeight: 'normal', color: '#888', fontSize: '12px' }}>(% of calls)</span>
								</div>
								{inDrilldown && (
									<div
										onClick={() => this.setState({ drilldownMenu: null })}
										style={{ fontSize: '12px', color: '#2A6AA9', cursor: 'pointer', textDecoration: 'underline' }}
									>← Back</div>
								)}
							</div>
							{canAppendFromChart && (
								<div data-pdf-hide="true" style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Tip: click a bar to limit the report to callers who hit it.</div>
							)}
							<div style={{ cursor: (canDrilldown || canAppendFromChart) ? 'pointer' : 'default' }}>
								<ResponsiveContainer width="100%" height={hbarHeight}>
									<BarChart layout="vertical" data={hbarData} margin={{ top: 8, right: 60, left: 0, bottom: 8 }} onClick={chartOnClick}>
										<CartesianGrid strokeDasharray="3 3" horizontal={false} />
										<XAxis type="number" domain={[0, 100]} tickFormatter={(v) => v + '%'} tick={{ fontSize: 10 }} />
										<YAxis type="category" dataKey="period" width={yAxisWidth} tick={{ fontSize: 10 }} interval={0} />
										<Tooltip formatter={(value, name, props) => {
											var p = props && props.payload ? props.payload : {};
											return [(p.pct != null ? p.pct.toFixed(1) + '%' : '') + ' (' + (p.count != null ? p.count.toLocaleString() : '') + ' calls)', 'Share'];
										}} />
										<Bar dataKey="pct" name="% of Calls" radius={[0, 2, 2, 0]}>
											{hbarData.map((d, i) => (
												<Cell key={i} fill={(d.kind || '').indexOf('context') === 0 ? '#9aa6b2' : '#2A6AA9'} />
											))}
											<LabelList dataKey="pct" position="right" formatter={(v) => v.toFixed(1) + '%'} style={{ fontSize: 10, fill: '#555' }} />
										</Bar>
									</BarChart>
								</ResponsiveContainer>
							</div>
						</div>
					);
				}

				var byType = { bar: bar, hbar: hbar, pie: pie, heatmap: heat };
				// If the saved chartType isn't available for this report, fall back
				// to the first available one.
				var activeType = availableTypes.indexOf(this.state.chartType) !== -1
					? this.state.chartType
					: availableTypes[0];
				var selected = byType[activeType] || bar || hbar || pie || heat;

				if (bar || hbar || pie || heat) {
					visualization = (
						<div>
							{availableTypes.length > 1 && (
								<div data-pdf-hide="true" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
									<label style={{ fontSize: '13px', color: '#555' }}>Chart Type:</label>
									<select
										value={activeType}
										onChange={(e) => this.setState({ chartType: e.target.value })}
										style={{ fontSize: '13px', padding: '4px 8px' }}
									>
										{availableTypes.indexOf('bar') !== -1 && <option value="bar">Bar Chart</option>}
										{availableTypes.indexOf('hbar') !== -1 && <option value="hbar">Horizontal Bars (% scale)</option>}
										{availableTypes.indexOf('pie') !== -1 && <option value="pie">Pie Chart</option>}
										{availableTypes.indexOf('heatmap') !== -1 && <option value="heatmap">Heatmap</option>}
									</select>
								</div>
							)}
							{selected}
						</div>
					);
				}
			}

			// Table + pagination
			var tableSection = null;
			var table = reportData ? reportData.table : null;
			// Per-response flags: hideFirstColumn drops col 0 (used for callID),
			// rowDetail makes rows clickable to open the call timeline. Summary
			// reports leave both off and don't paginate (their pageSize === total).
			var hideFirstColumn = !!(reportData && reportData.hideFirstColumn);
			var rowDetail = !!(reportData && reportData.rowDetail);

			// Drilldown reports show two table shapes that mirror the chart:
			//   - No menu selected → one row per menu (Menu, Selections totals)
			//   - Menu selected    → that menu's options, with the redundant Menu
			//                        column dropped
			if (table && reportData.optionsByMenu) {
				if (this.state.drilldownMenu) {
					var dm = this.state.drilldownMenu;
					var filteredRows = table.rows
						.filter(function (r) { return r[0] === dm; })
						.map(function (r) { return r.slice(1); });
					table = Object.assign({}, table, {
						columns: table.columns.slice(1),
						rows: filteredRows,
						total: filteredRows.length,
						pageSize: filteredRows.length || 1
					});
				} else {
					var primaryHeader = (reportData.chart && reportData.chart.primaryAxis) || 'Group';
					var countHeader = (reportData.chart && reportData.chart.barLabel) || 'Count';
					var menuRows = ((reportData.chart && reportData.chart.data) || [])
						.map(function (d) { return [d.period, d.count]; });
					table = {
						columns: [primaryHeader, countHeader],
						rows: menuRows,
						total: menuRows.length,
						page: 0,
						pageSize: menuRows.length || 1
					};
				}
			}
			if (table) {
				if (table.rows.length > 0) {
					var totalPages = Math.ceil(table.total / table.pageSize);
					var cur = table.page;
					var rowStart = cur * table.pageSize + 1;
					var rowEnd = Math.min(rowStart + table.rows.length - 1, table.total);
					var showPaging = table.total > table.pageSize;

					var displayCols = hideFirstColumn ? table.columns.slice(1) : table.columns;
					var colOffset = hideFirstColumn ? 1 : 0;

					var sortCol = this.state.sortCol;
					var sortDir = this.state.sortDir;
					var rowAppendFilter = table.rowAppendFilter || null;
					var contextRowCount = table.contextRowCount || 0;
					// Context rows (when present) stay pinned to the top — sort
					// only the body rows so the summary lines remain anchored.
					var contextSlice = table.rows.slice(0, contextRowCount);
					var bodyRows = table.rows.slice(contextRowCount);
					if (sortCol !== null) {
						var sortIdx = sortCol + colOffset;
						bodyRows.sort(function (a, b) {
							var av = a[sortIdx] != null ? a[sortIdx] : '';
							var bv = b[sortIdx] != null ? b[sortIdx] : '';
							if (av < bv) return sortDir === 'asc' ? -1 : 1;
							if (av > bv) return sortDir === 'asc' ? 1 : -1;
							return 0;
						});
					}
					var sortedRows = contextSlice.concat(bodyRows);

					var thSortStyle = Object.assign({}, thStyle, { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' });

					// Identify which trailing display columns are dynamic meta columns
					// (added via the "+" picker). They get an "×" removal control in
					// the header. Only applies to the Interaction Detail report.
					var isInteractionDetail = rep && rep.route === 'advancedIncomingCallsDetail';
					var extraMetaTypes = isInteractionDetail ? (this.state.extraMetaTypes || []) : [];
					var firstMetaColIdx = extraMetaTypes.length ? displayCols.length - extraMetaTypes.length : -1;

					var hint = rowDetail
						? ' — click a row to view call detail'
						: (rowAppendFilter ? ' — click a row to limit the report to callers who hit it' : '');
					tableSection = (
						<div>
							<div style={{ marginBottom: '6px', color: '#555', fontSize: '12px' }}>
								{'Showing rows ' + rowStart + '–' + rowEnd + ' of ' + table.total + hint}
							</div>
							<div style={{ overflowX: 'auto' }}>
								<table style={tableStyle}>
									<thead>
										<tr>
											{displayCols.map((col, i) => {
												var sorted = sortCol === i;
												var arrow = sorted ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
												var isMeta = firstMetaColIdx >= 0 && i >= firstMetaColIdx;
												var metaIdx = isMeta ? i - firstMetaColIdx : -1;
												var metaTypeForCol = isMeta ? extraMetaTypes[metaIdx] : null;
												return (
													<th key={i} style={thSortStyle} onClick={() => this.handleSort(i)}>
														{col}{arrow}
														{metaTypeForCol && (
															<span
																title="Remove column"
																onClick={(e) => { e.stopPropagation(); this.removeMetaColumn(metaTypeForCol.id); }}
																style={{ marginLeft: '6px', color: '#888', cursor: 'pointer', fontWeight: 'bold' }}
															>×</span>
														)}
													</th>
												);
											})}
											{isInteractionDetail && (
												<th
													style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', padding: '6px 10px', color: '#2A6AA9', fontWeight: 'bold' }}
													title="Add a column from connectionMeta"
													onClick={() => this.setState({ metaPickerOpen: true })}
												>＋</th>
											)}
										</tr>
									</thead>
									<tbody>
										{sortedRows.map((row, ri) => {
											// Context rows (All Calls / Hit none) sit at the top of the
											// table when a cohort/meta filter is active — they're
											// summary context, not actionable, so we disable clicks
											// and give them a distinct gray style.
											var isContext = ri < contextRowCount;
											var meta = (!isContext && rowDetail && table.rowMeta) ? table.rowMeta[ri] : null;
											var canOpen = !!(meta && meta.callID);
											var rowAppendValue = (!isContext && rowAppendFilter) ? row[rowAppendFilter.col] : null;
											var canAppendFromRow = !canOpen && !!(rowAppendFilter && rowAppendValue);
											var canClick = canOpen || canAppendFromRow;
											var displayRow = hideFirstColumn ? row.slice(1) : row;
											var baseStyle = isContext
												? { backgroundColor: '#eef2f6', fontStyle: 'italic', color: '#4a5560', borderTop: ri === 0 ? '1px solid #d8e2ec' : 'none', borderBottom: ri === contextRowCount - 1 ? '2px solid #c8d4df' : 'none' }
												: (canClick
													? (ri % 2 === 0 ? trEvenStyle : trOddStyle)
													: { backgroundColor: ri % 2 === 0 ? '#f5f8fc' : '#ffffff' });
											var clickHandler = canOpen
												? () => this.openCallDetail(meta.callID, meta.phone, meta.callStart)
												: (canAppendFromRow ? () => this.appendFilterValue(rowAppendFilter.name, rowAppendValue) : undefined);
											return (
												<tr
													key={ri}
													style={baseStyle}
													onClick={clickHandler}
													onMouseEnter={canClick ? (e) => { e.currentTarget.style.backgroundColor = '#d6e8f7'; } : undefined}
													onMouseLeave={canClick ? (e) => { e.currentTarget.style.backgroundColor = ri % 2 === 0 ? trEvenStyle.backgroundColor : trOddStyle.backgroundColor; } : undefined}
												>
													{displayRow.map((cell, ci) => {
														var col = displayCols[ci];
														var formatted = cell;
														if (cell != null) {
															if (col === 'Date and Time' || col === 'Call Start' || col === 'Call End' || col === 'Last Picked') formatted = this.formatDateTime(cell);
															else if (col === 'Phone Number' || col === 'Transfer Number') formatted = this.formatPhone(cell);
														}
														return <td key={ci} style={tdStyle}>{formatted != null ? formatted : ''}</td>;
													})}
													{/* Empty cell aligned with the "+" header so widths match */}
													{isInteractionDetail && <td style={tdStyle}></td>}
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
							<div data-pdf-hide="true" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '10px 0' }}>
								{showPaging && (
									<React.Fragment>
										<div
											className="button"
											style={{ padding: '5px 18px', opacity: cur == 0 ? 0.4 : 1, cursor: cur == 0 ? 'default' : 'pointer' }}
											onClick={cur > 0 ? () => this.changePage(cur - 1) : undefined}
										>
											← Prev
										</div>
										<div style={{ fontSize: '13px', color: '#555' }}>
											Page {cur + 1} of {totalPages}
										</div>
										<div
											className="button"
											style={{ padding: '5px 18px', opacity: cur >= totalPages - 1 ? 0.4 : 1, cursor: cur >= totalPages - 1 ? 'default' : 'pointer' }}
											onClick={cur < totalPages - 1 ? () => this.changePage(cur + 1) : undefined}
										>
											Next →
										</div>
									</React.Fragment>
								)}
								<div
									className="button"
									style={{ padding: '5px 18px' }}
									onClick={this.exportReport}
								>
									Export CSV
								</div>
								<div
									className="button"
									style={{ padding: '5px 18px', opacity: this.state.pdfGenerating ? 0.5 : 1, cursor: this.state.pdfGenerating ? 'default' : 'pointer' }}
									onClick={this.state.pdfGenerating ? undefined : this.exportPDF}
									title="Export the visible report (chart + table) as a PDF"
								>
									{this.state.pdfGenerating ? 'Generating PDF…' : 'Export PDF'}
								</div>
							</div>
						</div>
					);
				} else {
					tableSection = <div style={{ color: '#888', padding: '14px 0', fontSize: '14px' }}>No results returned.</div>;
				}
			}

			// Filter chips, grouped into labeled sections. Chips are derived
			// from state so they render before the report runs. Each section's
			// × removes just that chip via removeFilter(paramName, itemKey).
			var filterStrip = null;
			var isBreadcrumbSummary = rep && rep.route === 'advancedBreadcrumbSummary';
			var stateParams = this.state.parameters || {};
			var labelOptions    = (this.props.data && this.props.data.breadcrumbLabels) || [];
			var metaTypeOptions = (this.props.data && this.props.data.connectionMetaTypes) || [];
			var menuList  = (this.props.data && this.props.data.menus) || [];
			var metaDraft = this.state.metaFilterDraft;
			var canShowAdders = isBreadcrumbSummary;

			// Each section: { heading, items: [{ label, itemKey, paramName }] }.
			// Sections are only rendered when they have at least one chip.
			var chipSections = [];
			if (Array.isArray(stateParams.cohortBreadcrumbs) && stateParams.cohortBreadcrumbs.length > 0) {
				chipSections.push({
					heading: 'Callers who hit',
					items: stateParams.cohortBreadcrumbs.map(function (c) {
						return { paramName: 'cohortBreadcrumbs', itemKey: c, label: c };
					})
				});
			}
			if (Array.isArray(stateParams.cohortMenus) && stateParams.cohortMenus.length > 0) {
				chipSections.push({
					heading: 'Callers who went through',
					items: stateParams.cohortMenus.map(function (m) {
						return {
							paramName: 'cohortMenus',
							itemKey: String(m.menu),
							label: m.label || m.menu
						};
					})
				});
			}
			if (Array.isArray(stateParams.metaFilters) && stateParams.metaFilters.length > 0) {
				chipSections.push({
					heading: 'Caller metadata',
					items: stateParams.metaFilters.map(function (m) {
						return {
							paramName: 'metaFilters',
							itemKey: m.typeId + ':' + m.value,
							label: (m.typeName || ('Type ' + m.typeId)) + ': ' + m.value
						};
					})
				});
			}
			if (Array.isArray(stateParams.includeBreadcrumbs) && stateParams.includeBreadcrumbs.length > 0) {
				chipSections.push({
					heading: 'Show breadcrumbs',
					items: stateParams.includeBreadcrumbs.map(function (c) {
						return { paramName: 'includeBreadcrumbs', itemKey: c, label: c };
					})
				});
			}
			if (Array.isArray(stateParams.includeMenus) && stateParams.includeMenus.length > 0) {
				chipSections.push({
					heading: 'Show menus',
					items: stateParams.includeMenus.map(function (m) {
						return {
							paramName: 'includeMenus',
							itemKey: String(m.menu),
							label: m.label || m.menu
						};
					})
				});
			}

			if (chipSections.length > 0 || canShowAdders) {
				var headingStyle = { fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', minWidth: '130px', textAlign: 'right', paddingRight: '8px' };
				var sectionRowStyle = { display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '6px' };
				var chipStyle = { display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#e3effa', borderRadius: '14px', padding: '4px 4px 4px 12px', fontSize: '12px', color: '#1f4e7a' };
				var chipXStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', borderRadius: '50%', background: '#fff', color: '#888', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' };

				filterStrip = (
					<div style={{ marginBottom: '12px', background: '#fafcfe', border: '1px solid #e1eaf2', borderRadius: '6px', padding: '8px 12px' }}>
						{chipSections.map((sec) => (
							<div key={sec.heading} style={sectionRowStyle}>
								<div style={headingStyle}>{sec.heading}:</div>
								{sec.items.map((chip, i) => (
									<div key={i} style={chipStyle}>
										<span><strong>{chip.label}</strong></span>
										<span
											onClick={() => this.removeFilter(chip.paramName, chip.itemKey)}
											title="Remove filter"
											style={chipXStyle}
										>×</span>
									</div>
								))}
							</div>
						))}
						{canShowAdders && (() => {
							var addRowStyle = { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', fontSize: '12px', color: '#555', marginBottom: '4px' };
							var addContainerStyle = { marginTop: chipSections.length > 0 ? '4px' : 0, paddingTop: chipSections.length > 0 ? '6px' : 0, borderTop: chipSections.length > 0 ? '1px dashed #d8e2ec' : 'none' };
							var selectStyle = { fontSize: '12px', padding: '4px 8px', maxWidth: '240px' };
							return (
								<div data-pdf-hide="true" style={addContainerStyle}>
									<div style={addRowStyle}>
										<span style={headingStyle}>Breadcrumbs:</span>
										<select
											value=""
											onChange={(e) => { var v = e.target.value; e.target.value = ''; if (v) this.appendFilterValue('cohortBreadcrumbs', v); }}
											style={selectStyle}
											title="Restrict the report to callers who hit a specific breadcrumb"
										>
											<option value="">+ Callers who hit…</option>
											{labelOptions.map((label, i) => <option key={i} value={label}>{label}</option>)}
										</select>
										<select
											value=""
											onChange={(e) => { var v = e.target.value; e.target.value = ''; if (v) this.appendFilterValue('includeBreadcrumbs', v); }}
											style={selectStyle}
											title="Restrict the breakdown to specific breadcrumbs (does not change the caller set)"
										>
											<option value="">+ Show breadcrumb…</option>
											{labelOptions.map((label, i) => <option key={i} value={label}>{label}</option>)}
										</select>
									</div>
									<div style={addRowStyle}>
										<span style={headingStyle}>Menus:</span>
										<select
											value=""
											onChange={(e) => {
												var v = e.target.value; e.target.value = '';
												if (!v) return;
												var mo = menuList.find(function (x) { return x.key === v; });
												if (mo) this.appendMenuFilter('cohortMenus', mo);
											}}
											style={selectStyle}
											title="Restrict the report to callers who went through a specific menu"
										>
											<option value="">+ Callers who went through…</option>
											{menuList.map((mo, i) => <option key={i} value={mo.key}>{mo.label}</option>)}
										</select>
										<select
											value=""
											onChange={(e) => {
												var v = e.target.value; e.target.value = '';
												if (!v) return;
												var mo = menuList.find(function (x) { return x.key === v; });
												if (mo) this.appendMenuFilter('includeMenus', mo);
											}}
											style={selectStyle}
											title="Add specific menu rows to the breakdown"
										>
											<option value="">+ Show menu…</option>
											{menuList.map((mo, i) => <option key={i} value={mo.key}>{mo.label}</option>)}
										</select>
									</div>
									<div style={addRowStyle}>
										<span style={headingStyle}>Metadata:</span>
										{!metaDraft && (
											<span
												className="button"
												style={{ padding: '4px 10px', fontSize: '12px' }}
												onClick={() => this.setState({ metaFilterDraft: { typeId: '', value: '' } })}
											>+ Caller metadata</span>
										)}
										{metaDraft && (
											<span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f5f8fc', borderRadius: '4px', padding: '4px 8px' }}>
												<select
													value={metaDraft.typeId}
													onChange={(e) => this.setState({ metaFilterDraft: Object.assign({}, metaDraft, { typeId: e.target.value }) })}
													style={{ fontSize: '12px', padding: '3px 6px' }}
												>
													<option value="">Meta type…</option>
													{metaTypeOptions.map((t, i) => <option key={i} value={t.id}>{t.name}</option>)}
												</select>
												<input
													type="text"
													placeholder="value"
													value={metaDraft.value}
													onChange={(e) => this.setState({ metaFilterDraft: Object.assign({}, metaDraft, { value: e.target.value }) })}
													onKeyDown={(e) => { if (e.key === 'Enter') { var t = metaTypeOptions.find(function (x) { return String(x.id) === String(metaDraft.typeId); }); this.appendMetaFilter(metaDraft.typeId, t ? t.name : null, metaDraft.value); } }}
													style={{ fontSize: '12px', padding: '3px 6px', width: '120px' }}
												/>
												<span
													className="button"
													style={{ padding: '3px 10px', fontSize: '12px' }}
													onClick={() => { var t = metaTypeOptions.find(function (x) { return String(x.id) === String(metaDraft.typeId); }); this.appendMetaFilter(metaDraft.typeId, t ? t.name : null, metaDraft.value); }}
												>Add</span>
												<span
													onClick={() => this.setState({ metaFilterDraft: null })}
													title="Cancel"
													style={{ cursor: 'pointer', color: '#888', fontWeight: 'bold', fontSize: '14px', padding: '0 4px' }}
												>×</span>
											</span>
										)}
									</div>
								</div>
							);
						})()}
					</div>
				);
			}

			// KPI tile strip — server emits an ordered array of { label, value,
			// format }. The client formats based on the hint so each report can
			// declare arbitrary KPIs without the client knowing their names.
			var kpiStrip = null;
			if (reportData && reportData.kpis && reportData.kpis.length > 0) {
				var formatKPI = (val, fmt) => {
					if (val === null || val === undefined || val === '') return '—';
					if (fmt === 'number') {
						var n = parseFloat(val);
						return isNaN(n) ? String(val) : n.toLocaleString();
					}
					if (fmt === 'decimal1') {
						var n2 = parseFloat(val);
						return isNaN(n2) ? String(val) : n2.toFixed(1);
					}
					if (fmt === 'phone') return this.formatPhone(val);
					// 'duration' is preformatted server-side; anything else passes through
					return String(val);
				};
				kpiStrip = (
					<div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
						{reportData.kpis.map((kpi, i) => {
							var display = formatKPI(kpi.value, kpi.format);
							return (
								<div key={i} style={{ flex: 1, background: '#f5f8fc', borderRadius: '6px', padding: '14px 16px', borderLeft: '3px solid #2A6AA9', minWidth: 0 }}>
									<div style={{ fontSize: '24px', fontWeight: '700', color: '#2A6AA9', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={display}>{display}</div>
									<div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={kpi.label}>{kpi.label}</div>
								</div>
							);
						})}
					</div>
				);
			}

			// Overrides on .pop: default class caps at 60vh with heavy padding and
			// centered text. Widen to 96vw, raise the cap to 88vh, trim padding,
			// and shift slightly below the vertical center so the modal clears
			// the domain/user nav at its tallest. Letting the modal size to its
			// content (no fixed height, no flex column) keeps it compact when
			// no report has run yet — it'll grow up to the 88vh cap once
			// content arrives, after which the CSS's overflow:auto scrolls.
			var popStyle = {
				position: 'fixed',
				top: '54%',
				left: '50%',
				transform: 'translate(-50%, -50%)',
				width: '96vw',
				maxWidth: 'none',
				maxHeight: '88vh',
				padding: '12px 18px 18px',
				textAlign: 'left',
				boxSizing: 'border-box'
			};
			modal = (
				<div className="blackout">
					<div className="pop" style={popStyle}>
						<div className="viewCloser" style={{ display: 'block' }} onClick={this.closeReport} />

						{/* Compact header bar — small caps name on the left */}
						<div style={{ display: 'flex', alignItems: 'center', paddingBottom: '8px', marginBottom: '10px', paddingRight: '40px' }}>
							<div style={{ fontSize: '12px', fontWeight: '700', color: '#2A6AA9', textTransform: 'uppercase', letterSpacing: '1.2px' }}>{rep.name}</div>
						</div>

						<div style={{ display: 'flex', flexFlow: 'row nowrap', alignItems: 'flex-end', margin: '0 0 12px', gap: '8px' }}>
							{applicationField}
							{paramFields}
							<div className="button" style={{ flex: '1 1 100px', minWidth: '50px' }} onClick={this.runReport}>Start</div>
						</div>

						<div ref={this.reportContentRef}>
							{filterStrip}
							{loading
								? <div className="diradLoader" style={{ margin: '60px auto' }}>
									<DiradLogo className="diradLogo" aria-label="DiRAD" role="img" />
									<div className="diradBar" />
								</div>
								: <div>{kpiStrip}{visualization}{tableSection}</div>
							}
						</div>
					</div>
				</div>
			);
		}

		// Schedule modal — captures frequency, time, day, and recipients for the
		// selected report. Save persists to the server; the actual cron runner that
		// fires schedules + emails the report is a follow-up piece.
		var scheduleModal = null;
		if (this.state.schedulingReport && this.state.tempSchedule) {
			var sr = this.state.schedulingReport;
			var s = this.state.tempSchedule;
			var labelStyle = { display: 'block', fontSize: '12px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' };
			var inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '14px' };
			var paramInputStyle = Object.assign({}, inputStyle, { marginBottom: 0, height: 'auto' });
			scheduleModal = (
				<div className="blackout">
					<div className="pop" style={{ maxWidth: '460px' }}>
						<div className="viewCloser" style={{ display: 'block' }} onClick={this.closeSchedule} />
						<div style={{ fontSize: '20px', padding: '15px 15px 5px' }}>Schedule Report</div>
						<div style={{ fontSize: '13px', color: '#555', padding: '0 15px 15px' }}>{sr.name}</div>
						<div style={{ padding: '0 15px 15px', textAlign: 'left' }}>
							{(sr.parameters || []).length > 0 && (
								<div style={{ marginBottom: '14px', padding: '12px', background: '#f5f8fc', borderLeft: '3px solid #2A6AA9', borderRadius: '4px' }}>
									<div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px', fontWeight: '600' }}>Report Parameters</div>
									{(sr.parameters || []).map(function (p) {
										return (
											<div key={p.parameter} style={{ marginBottom: '10px' }}>
												<label style={labelStyle}>{p.label}</label>
												{this.renderScheduleParamInput(p, paramInputStyle)}
											</div>
										);
									}.bind(this))}
								</div>
							)}

							<label style={labelStyle}>Frequency</label>
							<select value={s.frequency} onChange={this.editSchedule('frequency')} style={inputStyle}>
								<option value="daily">Daily</option>
								<option value="weekly">Weekly</option>
								<option value="monthly">Monthly</option>
							</select>

							<label style={labelStyle}>Time of Day</label>
							<input type="time" value={s.time} onChange={this.editSchedule('time')} style={inputStyle} />

							{s.frequency === 'weekly' && (
								<div>
									<label style={labelStyle}>Day of Week</label>
									<select value={s.dayOfWeek} onChange={this.editSchedule('dayOfWeek')} style={inputStyle}>
										<option value="0">Sunday</option>
										<option value="1">Monday</option>
										<option value="2">Tuesday</option>
										<option value="3">Wednesday</option>
										<option value="4">Thursday</option>
										<option value="5">Friday</option>
										<option value="6">Saturday</option>
									</select>
								</div>
							)}

							{s.frequency === 'monthly' && (
								<div>
									<label style={labelStyle}>Day of Month</label>
									<input type="number" min="1" max="31" value={s.dayOfMonth} onChange={this.editSchedule('dayOfMonth')} style={inputStyle} />
								</div>
							)}

							<label style={labelStyle}>Email Recipients</label>
							<input
								type="text"
								value={s.recipients}
								onChange={this.editSchedule('recipients')}
								placeholder="user@example.com, another@example.com"
								style={inputStyle}
							/>

							<div className="button" onClick={this.saveSchedule} style={{ marginTop: '6px' }}>Save Schedule</div>
						</div>
					</div>
				</div>
			);
		}

		// Schedules manager modal — lists existing scheduled reports with delete
		var schedulesManagerModal = null;
		if (this.state.schedulesManagerOpen) {
			var allSchedules = ((this.props.data && this.props.data.AdvancedReports && this.props.data.AdvancedReports.schedules) || {});
			var scheduleList = Object.keys(allSchedules).map(function (id) { return allSchedules[id]; });
			scheduleList.sort(function (a, b) { return (a.reportName || '').localeCompare(b.reportName || ''); });

			schedulesManagerModal = (
				<div className="blackout">
					<div className="pop" style={{ maxWidth: '640px' }}>
						<div className="viewCloser" style={{ display: 'block' }} onClick={this.closeSchedulesManager} />
						<div style={{ fontSize: '20px', padding: '15px 15px 5px' }}>Scheduled Reports</div>
						<div style={{ fontSize: '12px', color: '#888', padding: '0 15px 12px' }}>{scheduleList.length} scheduled</div>
						<div style={{ padding: '0 15px 15px', textAlign: 'left' }}>
							{scheduleList.length === 0
								? <div style={{ fontSize: '13px', color: '#888', padding: '20px 0', textAlign: 'center' }}>No scheduled reports yet. Click Schedule on a report row to create one.</div>
								: scheduleList.map(function (sch) {
									return (
										<div key={sch.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', border: '1px solid #e3e7ee', borderRadius: '6px', marginBottom: '6px', background: '#fafbfd' }}>
											<div style={{ flex: 1, minWidth: 0 }}>
												<div style={{ fontSize: '14px', fontWeight: '600', color: '#2A6AA9' }}>{sch.reportName || sch.reportRoute}</div>
												<div style={{ fontSize: '12px', color: '#555', marginTop: '2px' }}>{formatScheduleSummary(sch)}</div>
												{sch.recipients && (
													<div style={{ fontSize: '11px', color: '#888', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sch.recipients}>
														→ {sch.recipients}
													</div>
												)}
											</div>
											<div
												onClick={this.deleteSchedule(sch.id)}
												style={{
													padding: '5px 12px',
													fontSize: '12px',
													fontWeight: '600',
													color: '#c0392b',
													background: '#fdecea',
													border: '1px solid #f5c6c0',
													borderRadius: '4px',
													cursor: 'pointer',
													flexShrink: 0
												}}
												onMouseEnter={(e) => { e.currentTarget.style.background = '#fadbd8'; }}
												onMouseLeave={(e) => { e.currentTarget.style.background = '#fdecea'; }}
											>
												Delete
											</div>
										</div>
									);
								}.bind(this))
							}
						</div>
					</div>
				</div>
			);
		}

		return (
			<div>
				<table style={{ width: '100%' }}>
					<thead>
						<tr className="dataRow" style={{ justifyContent: 'flex-start' }}>
							<th style={{ width: '270px', flexShrink: 0, textAlign: 'left' }}>Report</th>
							<th style={{ flex: 1, textAlign: 'left' }}>Description</th>
							<th style={{ width: '130px', flexShrink: 0, textAlign: 'right' }} />
						</tr>
					</thead>
					<tbody>
						{reportRows}
					</tbody>
				</table>
				<img
					src={this.props.img.gear}
					className="plusButton"
					alt="Manage scheduled reports"
					title="Manage scheduled reports"
					onClick={this.openSchedulesManager}
				/>
				{modal}
				{scheduleModal}
				{schedulesManagerModal}
				{this.state.activeCallID ? this.renderCallDetail() : null}
				{this.state.metaPickerOpen && this.renderMetaPicker()}
			</div>
		);
	}

	renderMetaPicker = () => {
		var all = (this.props.data && this.props.data.connectionMetaTypes) || [];
		var taken = {};
		(this.state.extraMetaTypes || []).forEach(function (m) { taken[m.id] = true; });
		var available = all.filter(function (t) { return !taken[t.id]; });
		return (
			<div className="blackout" tabIndex="1" autoFocus style={{ zIndex: 2050 }}>
				<div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '420px', maxWidth: '94vw', maxHeight: '80vh', padding: '0', position: 'relative', overflow: 'hidden' }}>
					<div className="viewCloser" style={{ display: 'block' }} onClick={() => this.setState({ metaPickerOpen: false })} />
					<div style={{ background: '#002f5f', padding: '22px 28px 16px', color: '#fff', flexShrink: 0 }}>
						<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '5px' }}>Add Column</div>
						<div style={{ fontSize: '21px', fontWeight: '700' }}>Connection Meta Types</div>
					</div>
					<div style={{ padding: '12px 18px 18px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
						{available.length === 0 && (
							<div style={{ padding: '10px', color: '#777', fontSize: '13px' }}>
								{all.length === 0 ? 'Loading…' : 'All available meta types have been added.'}
							</div>
						)}
						{available.map((t) => (
							<div
								key={t.id}
								onClick={() => this.addMetaColumn(t)}
								style={{ padding: '8px 10px', borderBottom: '1px solid #eef2f6', cursor: 'pointer', fontSize: '14px' }}
								onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f6fb'; }}
								onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
							>
								{t.name}
								<span style={{ color: '#999', fontSize: '11px', marginLeft: '6px' }}>(id {t.id})</span>
							</div>
						))}
					</div>
				</div>
			</div>
		);
	}
}

export { AdvancedReports, icon, roles };
export default AdvancedReports;
